import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Award,
  Bell,
  Shield,
  Search,
  Edit2,
  Trash2,
  UserPlus,
  Plus,
  Lock,
  Key,
  Mail,
  Smartphone,
  Globe,
  Clock,
  Save,
  ToggleLeft,
  ToggleRight,
  X,
  Coins,
  Gift,
  Star,
  Upload,
  Calendar,
  Cake,
  Briefcase,
  Heart,
  UserCog,
  Eye,
  PlusCircle,
  Pencil,
  Check,
  Plug,
  Copy,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Newspaper,
  Quote,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  AlertTriangle,
  Info,
  Settings2,
  MoreVertical,
  PartyPopper,
  Link,
  Repeat,
  Trophy,
  Sparkles,
  Crown,
  Gem,
  LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
// Get refreshUser for updating current user data after waves management
import { Card, CardContent, Avatar, Badge, Button, Modal } from '../../components/UI';
import FormModal from '../../components/FormModal';
import { usersApi, appreciationsApi, rewardsApi, eventsApi, settingsApi, xoxodayApi, autoWavesApi, integrationsApi } from '../../services/api';
import { CompanyValue, Event, EventTemplate, AnnouncementTemplate, RecurringFrequency, EventTypeConfig } from '../../types';
import { renderValueIcon, ICON_OPTIONS, isLucideIcon } from '../../utils/iconMap';
import { EVENT_ICON_OPTIONS, getEventIconComponent } from '../../utils/eventIcons';
import styles from './Settings.module.css';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  role: string;
  is_active: boolean;
  points_balance: number;
  created_at: string;
  mentor_id?: string;
  mentor_first_name?: string;
  mentor_last_name?: string;
  birthday?: string;
  joining_date?: string;
}

// Helper function to display role correctly
const formatRole = (role: string): string => {
  // Map 'manager' to 'Mentor' for display
  if (role === 'manager') return 'Mentor';
  if (role === 'superadmin') return 'Super Admin';
  return role.charAt(0).toUpperCase() + role.slice(1);
};

// Helper function to get mentor display text
const getMentorDisplay = (user: User): string => {
  if (user.role === 'admin' || user.role === 'superadmin') return 'Self';
  if (user.mentor_first_name && user.mentor_last_name) {
    return `${user.mentor_first_name} ${user.mentor_last_name}`;
  }
  return 'No mentor';
};

interface Reward {
  id: string;
  name: string;
  description: string;
  points_cost: number;
  points_cost_max?: number;
  quantity_available: number;
  image_url?: string;
  category: string;
  status: string;
  redirect_url?: string;
  created_at: string;
}

interface RippleType {
  id: string;
  name: string;
  key: string;
  description: string;
  icon: string;
  color: string;
  default_points: number;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

interface PermissionMatrix {
  feature_id: string;
  feature_name: string;
  feature_key: string;
  feature_description: string;
  category: string;
  permissions: {
    [role: string]: {
      can_view: boolean;
      can_create: boolean;
      can_edit: boolean;
      can_delete: boolean;
    };
  };
}

type TabType = 'users' | 'points' | 'rewards' | 'values' | 'ripple-types' | 'permissions' | 'notifications' | 'security' | 'integrations' | 'events';

// Auto-Waves Rule interface
interface AutoWavesRule {
  id: string;
  name: string;
  description?: string;
  rule_type: 'recurring' | 'birthday' | 'work_anniversary';
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  day_of_week?: number;
  day_of_month?: number;
  month_of_year?: number;
  waves_amount: number;
  target_type: 'all_active' | 'by_role' | 'specific_users';
  target_roles?: string[];
  target_user_ids?: string[];
  reason_template: string;
  is_active: boolean;
  last_executed_at?: string;
  next_execution_at?: string;
  execution_count: number;
  created_at: string;
  updated_at: string;
}

interface AutoWavesLog {
  id: string;
  rule_id: string;
  rule_name: string;
  rule_type: string;
  executed_at: string;
  users_processed: number;
  total_waves_distributed: number;
  status: 'success' | 'partial' | 'failed';
  error_message?: string;
  user_first_name?: string;
  user_last_name?: string;
}

const Settings: React.FC = () => {
  const { user: currentUser, refreshUser } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [companyValues, setCompanyValues] = useState<CompanyValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('associate');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddValueModal, setShowAddValueModal] = useState(false);
  const [showEditValueModal, setShowEditValueModal] = useState(false);
  const [showDeleteValueModal, setShowDeleteValueModal] = useState(false);
  const [selectedValue, setSelectedValue] = useState<CompanyValue | null>(null);
  const [valueFormData, setValueFormData] = useState({
    name: '',
    description: '',
    icon: '⭐',
    icon_url: '',
    color: '#0C6B62'
  });
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState<{ top: number; left: number } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: '',
    mentor_id: '',
    is_active: true,
    birthday: '',
    joining_date: '',
  });
  const [addUserFormData, setAddUserFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    role: 'associate',
    mentor_id: ''
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    emailAppreciations: true,
    emailAnnouncements: true,
    emailRewards: true,
    emailDigest: false,
    pushEnabled: true,
    pushAppreciations: true,
    pushAnnouncements: true,
    digestFrequency: 'weekly'
  });

  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    sessionTimeout: '30',
    passwordExpiry: '90',
    minPasswordLength: '8',
    requireSpecialChars: true,
    requireNumbers: true
  });

  // Security audit state
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<any>(null);
  const [expandedFinding, setExpandedFinding] = useState<string | null>(null);

  // Points management state (superadmin only)
  const [showManagePointsModal, setShowManagePointsModal] = useState(false);
  const [managePointsData, setManagePointsData] = useState({
    user_id: '',
    amount: '',
    operation: 'add' as 'add' | 'reduce',
    reason: ''
  });
  const [pointsSearchTerm, setPointsSearchTerm] = useState('');

  // Reset password state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  // Rewards management state (superadmin only)
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [rewardSearchTerm, setRewardSearchTerm] = useState('');
  const [showAddRewardModal, setShowAddRewardModal] = useState(false);
  const [showEditRewardModal, setShowEditRewardModal] = useState(false);
  const [showDeleteRewardModal, setShowDeleteRewardModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [rewardFormData, setRewardFormData] = useState({
    name: '',
    description: '',
    points_cost: '',
    points_cost_max: '',
    image_url: '',
    category: '',
    redirect_url: ''
  });
  const rewardImageInputRef = useRef<HTMLInputElement>(null);

  // Events Management state (superadmin only)
  const [customEvents, setCustomEvents] = useState<Event[]>([]);
  const [eventTemplates, setEventTemplates] = useState<EventTemplate[]>([]);
  const [announcementTemplates, setAnnouncementTemplates] = useState<AnnouncementTemplate[]>([]);
  // Admins land on "Create an Event" since other sub-tabs are superadmin-only on the backend
  const [eventsSubTab, setEventsSubTab] = useState<'event-types' | 'custom' | 'event-templates' | 'announcement-templates'>(
    currentUser?.role === 'admin' ? 'custom' : 'event-types'
  );
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showDeleteEventModal, setShowDeleteEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [expandedEventType, setExpandedEventType] = useState<string | null>(null);
  const [quickEditEventId, setQuickEditEventId] = useState<string | null>(null);
  const [quickEditData, setQuickEditData] = useState({ title: '', event_date: '' });
  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_type: 'custom',
    image_url: '',
    link_url: '',
    link_title: '',
    is_recurring: false,
    recurring_frequency: 'annually' as RecurringFrequency,
    recurring_day_of_week: 0,
    recurring_week_of_month: 1,
    start_time: '',
    end_time: '',
  });
  const [eventTypes, setEventTypes] = useState<EventTypeConfig[]>([]);
  const [showEventTypeModal, setShowEventTypeModal] = useState(false);
  const [editingEventType, setEditingEventType] = useState<EventTypeConfig | null>(null);
  const [eventTypeFormData, setEventTypeFormData] = useState({
    name: '', key: '', description: '', icon_name: 'party', color: '#6366F1'
  });
  const eventImageInputRef = useRef<HTMLInputElement>(null);
  const [editingEventTemplateId, setEditingEventTemplateId] = useState<string | null>(null);
  const [eventTemplateFormData, setEventTemplateFormData] = useState({
    title_template: '',
    message_template: '',
    icon_name: 'cake',
    image_url: '',
  });
  const eventTemplateImageInputRef = useRef<HTMLInputElement>(null);
  const [editingAnnouncementTemplateId, setEditingAnnouncementTemplateId] = useState<string | null>(null);
  const [announcementTemplateFormData, setAnnouncementTemplateFormData] = useState({
    title_template: '',
    message_template: '',
    image_url: '',
    auto_post_enabled: true,
    post_timing_days_before: 0,
  });
  const announcementTemplateImageInputRef = useRef<HTMLInputElement>(null);
  // Template Create modals
  const [showCreateEventTemplateModal, setShowCreateEventTemplateModal] = useState(false);
  const [showCreateAnnouncementTemplateModal, setShowCreateAnnouncementTemplateModal] = useState(false);
  const [createEventTemplateFormData, setCreateEventTemplateFormData] = useState({
    template_type: '',
    title_template: '',
    message_template: '',
    icon_name: 'party',
    image_url: '',
  });
  const [createAnnouncementTemplateFormData, setCreateAnnouncementTemplateFormData] = useState({
    template_type: '',
    title_template: '',
    message_template: '',
    image_url: '',
    auto_post_enabled: false,
    post_timing_days_before: 0,
  });
  const [eventsLoading, setEventsLoading] = useState(false);

  // Ripple Types management state (superadmin only)
  const [rippleTypes, setRippleTypes] = useState<RippleType[]>([]);
  const [showAddRippleTypeModal, setShowAddRippleTypeModal] = useState(false);
  const [showEditRippleTypeModal, setShowEditRippleTypeModal] = useState(false);
  const [showDeleteRippleTypeModal, setShowDeleteRippleTypeModal] = useState(false);
  const [selectedRippleType, setSelectedRippleType] = useState<RippleType | null>(null);
  const [rippleTypeFormData, setRippleTypeFormData] = useState({
    name: '',
    key: '',
    description: '',
    icon: '👏',
    color: '#0C6B62',
    default_points: '10'
  });

  // Role Permissions state (superadmin only)
  const [permissionsMatrix, setPermissionsMatrix] = useState<PermissionMatrix[]>([]);
  const [selectedPermissionRole, setSelectedPermissionRole] = useState<string>('admin');
  const [permissionChanges, setPermissionChanges] = useState<{ [key: string]: any }>({});

  // Xoxoday Integration state (superadmin only)
  const [xoxodayConfig, setXoxodayConfig] = useState<{
    configured: boolean;
    environment?: string;
    oauthUrl?: string;
    storeUrl?: string;
    tokenExpiresAt?: string;
    lastUpdated?: string;
  } | null>(null);
  const [xoxodayHealth, setXoxodayHealth] = useState<{
    status: string;
    tokenValid: boolean;
    daysUntilExpiry: number;
  } | null>(null);
  const [xoxodayTokenForm, setXoxodayTokenForm] = useState({
    client_id: '',
    client_secret: '',
    access_token: '',
    refresh_token: ''
  });
  const [showXoxodayTokenModal, setShowXoxodayTokenModal] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState<string | null>(null);

  // News Feed Integration state - Multiple sources
  interface IntegrationSource {
    id: string;
    provider: string;
    api_key?: string;
    category: string;
    is_enabled: boolean;
  }
  const [newsSources, setNewsSources] = useState<IntegrationSource[]>([]);
  const [showAddNewsModal, setShowAddNewsModal] = useState(false);
  const [showEditNewsModal, setShowEditNewsModal] = useState(false);
  const [selectedNewsSource, setSelectedNewsSource] = useState<IntegrationSource | null>(null);
  const [newsFormData, setNewsFormData] = useState({
    provider: 'newsapi',
    api_key: '',
    categories: ['business'] as string[],
    is_enabled: true
  });
  const [newsSaving, setNewsSaving] = useState<boolean>(false);

  // Quotes Integration state - Multiple sources
  const [quotesSources, setQuotesSources] = useState<IntegrationSource[]>([]);
  const [showAddQuotesModal, setShowAddQuotesModal] = useState(false);
  const [showEditQuotesModal, setShowEditQuotesModal] = useState(false);
  const [selectedQuotesSource, setSelectedQuotesSource] = useState<IntegrationSource | null>(null);
  const [quotesFormData, setQuotesFormData] = useState({
    provider: 'quotable',
    api_key: '',
    categories: ['motivational'] as string[],
    is_enabled: true
  });
  const [quotesSaving, setQuotesSaving] = useState<boolean>(false);

  // Expanded integration cards
  const [expandedIntegration, setExpandedIntegration] = useState<string | null>(null);
  const [integrationsLoading, setIntegrationsLoading] = useState<boolean>(false);

  // Auto-Waves state (superadmin only)
  const [autoWavesRules, setAutoWavesRules] = useState<AutoWavesRule[]>([]);
  const [autoWavesLogs, setAutoWavesLogs] = useState<AutoWavesLog[]>([]);
  const [showAddAutoWavesModal, setShowAddAutoWavesModal] = useState(false);
  const [showEditAutoWavesModal, setShowEditAutoWavesModal] = useState(false);
  const [showDeleteAutoWavesModal, setShowDeleteAutoWavesModal] = useState(false);
  const [selectedAutoWavesRule, setSelectedAutoWavesRule] = useState<AutoWavesRule | null>(null);
  const [autoWavesFormData, setAutoWavesFormData] = useState({
    name: '',
    description: '',
    rule_type: 'recurring' as 'recurring' | 'birthday' | 'work_anniversary',
    frequency: 'monthly' as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
    day_of_week: 1,
    day_of_month: 1,
    month_of_year: 1,
    waves_amount: 100,
    target_type: 'all_active' as 'all_active' | 'by_role' | 'specific_users',
    target_roles: [] as string[],
    reason_template: 'Auto-credited waves',
    is_active: true
  });
  const [autoWavesLoading, setAutoWavesLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'points') {
      fetchUsers();
      fetchAutoWavesData();
    } else if (activeTab === 'values') {
      fetchCompanyValues();
    } else if (activeTab === 'rewards') {
      fetchRewards();
    } else if (activeTab === 'ripple-types') {
      fetchRippleTypes();
    } else if (activeTab === 'permissions') {
      fetchPermissionsMatrix();
    } else if (activeTab === 'integrations') {
      fetchXoxodayData();
      fetchIntegrationSettings();
    } else if (activeTab === 'events') {
      fetchEventsData();
    }
  }, [activeTab]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersApi.getAll({});
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyValues = async () => {
    setLoading(true);
    try {
      const response = await appreciationsApi.getValues();
      setCompanyValues(response.data || []);
    } catch (error) {
      console.error('Failed to fetch company values:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetValueForm = () => {
    setValueFormData({
      name: '',
      description: '',
      icon: '⭐',
      icon_url: '',
      color: '#0C6B62'
    });
    setActionError('');
  };

  const handleValueIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 2MB for icons)
    if (file.size > 2 * 1024 * 1024) {
      setActionError('Icon size must be less than 2MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setActionError('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setValueFormData({ ...valueFormData, icon_url: base64, icon: '' });
      setActionError('');
    };
    reader.readAsDataURL(file);
  };

  const handleAddValue = async () => {
    if (!valueFormData.name || !valueFormData.description) {
      setActionError('Please fill in name and description');
      return;
    }

    // Must have either icon or icon_url
    if (!valueFormData.icon && !valueFormData.icon_url) {
      setActionError('Please provide an emoji icon or upload an icon image');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await appreciationsApi.createValue({
        name: valueFormData.name,
        description: valueFormData.description,
        icon: valueFormData.icon || undefined,
        icon_url: valueFormData.icon_url || undefined,
        color: valueFormData.color
      });
      setActionSuccess('Company value created successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowAddValueModal(false);
      resetValueForm();
      fetchCompanyValues();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to create company value');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditValue = (value: CompanyValue) => {
    setSelectedValue(value);
    setValueFormData({
      name: value.name,
      description: value.description,
      icon: value.icon || '⭐',
      icon_url: value.icon_url || '',
      color: value.color || '#0C6B62'
    });
    setActionError('');
    setShowEditValueModal(true);
  };

  const handleUpdateValue = async () => {
    if (!selectedValue) return;

    if (!valueFormData.name || !valueFormData.description) {
      setActionError('Please fill in name and description');
      return;
    }

    // Must have either icon or icon_url
    if (!valueFormData.icon && !valueFormData.icon_url) {
      setActionError('Please provide an emoji icon or upload an icon image');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await appreciationsApi.updateValue(selectedValue.id, {
        name: valueFormData.name,
        description: valueFormData.description,
        icon: valueFormData.icon || undefined,
        icon_url: valueFormData.icon_url || undefined,
        color: valueFormData.color
      });
      setActionSuccess('Company value updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowEditValueModal(false);
      resetValueForm();
      setSelectedValue(null);
      fetchCompanyValues();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update company value');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteValue = async () => {
    if (!selectedValue) return;

    setActionLoading(true);
    setActionError('');

    try {
      await appreciationsApi.deleteValue(selectedValue.id);
      setActionSuccess('Company value deleted successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowDeleteValueModal(false);
      setSelectedValue(null);
      fetchCompanyValues();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to delete company value');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchRewards = async () => {
    setLoading(true);
    try {
      const response = await rewardsApi.getAll({});
      setRewards(response.data.rewards || []);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRippleTypes = async () => {
    setLoading(true);
    try {
      const response = await settingsApi.getRippleTypes();
      setRippleTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ripple types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionsMatrix = async () => {
    setLoading(true);
    try {
      const response = await settingsApi.getPermissionsMatrix();
      setPermissionsMatrix(response.data.features || []);
    } catch (error) {
      console.error('Failed to fetch permissions matrix:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchXoxodayData = async () => {
    setLoading(true);
    try {
      const [configResponse, healthResponse] = await Promise.all([
        xoxodayApi.getConfig(),
        xoxodayApi.getHealth()
      ]);
      setXoxodayConfig(configResponse.data);
      setXoxodayHealth(healthResponse.data);
    } catch (error) {
      console.error('Failed to fetch Xoxoday data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateXoxodayTokens = async () => {
    // Only include non-empty fields
    const tokenData: any = {};
    if (xoxodayTokenForm.client_id) tokenData.client_id = xoxodayTokenForm.client_id;
    if (xoxodayTokenForm.client_secret) tokenData.client_secret = xoxodayTokenForm.client_secret;
    if (xoxodayTokenForm.access_token) tokenData.access_token = xoxodayTokenForm.access_token;
    if (xoxodayTokenForm.refresh_token) tokenData.refresh_token = xoxodayTokenForm.refresh_token;

    if (Object.keys(tokenData).length === 0) {
      setActionError('Please fill in at least one field');
      return;
    }

    setActionLoading(true);
    try {
      await xoxodayApi.updateTokens(tokenData);
      setActionSuccess('Tokens updated successfully!');
      setShowXoxodayTokenModal(false);
      setXoxodayTokenForm({ client_id: '', client_secret: '', access_token: '', refresh_token: '' });
      fetchXoxodayData(); // Refresh data
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update tokens');
      setTimeout(() => setActionError(''), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text: string, endpointName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(endpointName);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  // Integration settings functions
  const fetchIntegrationSettings = async () => {
    setIntegrationsLoading(true);
    try {
      const response = await integrationsApi.getAllSettings();
      const settings = response.data.data || [];

      // Group settings by integration type
      const newsItems = settings.filter((s: any) => s.integration_type === 'news');
      const quotesItems = settings.filter((s: any) => s.integration_type === 'quotes');

      setNewsSources(newsItems.map((s: any) => ({
        id: s.id,
        provider: s.provider,
        api_key: s.api_key || '',
        category: s.category || 'business',
        is_enabled: s.is_enabled
      })));

      setQuotesSources(quotesItems.map((s: any) => ({
        id: s.id,
        provider: s.provider,
        api_key: s.api_key || '',
        category: s.category || 'motivational',
        is_enabled: s.is_enabled
      })));
    } catch (error) {
      console.error('Failed to fetch integration settings:', error);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const resetNewsForm = () => {
    setNewsFormData({ provider: 'newsapi', api_key: '', categories: ['business'], is_enabled: true });
  };

  const resetQuotesForm = () => {
    setQuotesFormData({ provider: 'quotable', api_key: '', categories: ['motivational'], is_enabled: true });
  };

  // Toggle category selection for multi-select
  const toggleNewsCategory = (category: string) => {
    setNewsFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const toggleQuotesCategory = (category: string) => {
    setQuotesFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleAddNewsSource = async () => {
    setNewsSaving(true);
    setActionError('');
    try {
      await integrationsApi.saveSettings({
        integration_type: 'news',
        provider: newsFormData.provider,
        api_key: newsFormData.api_key || undefined,
        category: newsFormData.categories.join(','),
        is_enabled: newsFormData.is_enabled
      });
      setActionSuccess('News source added successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowAddNewsModal(false);
      resetNewsForm();
      fetchIntegrationSettings();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to add news source');
    } finally {
      setNewsSaving(false);
    }
  };

  const handleEditNewsSource = (source: any) => {
    setSelectedNewsSource(source);
    setNewsFormData({
      provider: source.provider,
      api_key: source.api_key || '',
      categories: source.category ? source.category.split(',') : ['business'],
      is_enabled: source.is_enabled
    });
    setShowEditNewsModal(true);
  };

  const handleUpdateNewsSource = async () => {
    if (!selectedNewsSource) return;
    setNewsSaving(true);
    setActionError('');
    try {
      await integrationsApi.saveSettings({
        integration_type: 'news',
        provider: newsFormData.provider,
        api_key: newsFormData.api_key || undefined,
        category: newsFormData.categories.join(','),
        is_enabled: newsFormData.is_enabled
      });
      setActionSuccess('News source updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowEditNewsModal(false);
      setSelectedNewsSource(null);
      resetNewsForm();
      fetchIntegrationSettings();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update news source');
    } finally {
      setNewsSaving(false);
    }
  };

  const handleDeleteNewsSource = async (provider: string) => {
    try {
      await integrationsApi.deleteSettingsByProvider('news', provider);
      setActionSuccess('News source removed successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      fetchIntegrationSettings();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to delete news source');
      setTimeout(() => setActionError(''), 3000);
    }
  };

  const handleAddQuotesSource = async () => {
    setQuotesSaving(true);
    setActionError('');
    try {
      await integrationsApi.saveSettings({
        integration_type: 'quotes',
        provider: quotesFormData.provider,
        api_key: quotesFormData.api_key || undefined,
        category: quotesFormData.categories.join(','),
        is_enabled: quotesFormData.is_enabled
      });
      setActionSuccess('Quotes source added successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowAddQuotesModal(false);
      resetQuotesForm();
      fetchIntegrationSettings();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to add quotes source');
    } finally {
      setQuotesSaving(false);
    }
  };

  const handleEditQuotesSource = (source: any) => {
    setSelectedQuotesSource(source);
    setQuotesFormData({
      provider: source.provider,
      api_key: source.api_key || '',
      categories: source.category ? source.category.split(',') : ['motivational'],
      is_enabled: source.is_enabled
    });
    setShowEditQuotesModal(true);
  };

  const handleUpdateQuotesSource = async () => {
    if (!selectedQuotesSource) return;
    setQuotesSaving(true);
    setActionError('');
    try {
      await integrationsApi.saveSettings({
        integration_type: 'quotes',
        provider: quotesFormData.provider,
        api_key: quotesFormData.api_key || undefined,
        category: quotesFormData.categories.join(','),
        is_enabled: quotesFormData.is_enabled
      });
      setActionSuccess('Quotes source updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowEditQuotesModal(false);
      setSelectedQuotesSource(null);
      resetQuotesForm();
      fetchIntegrationSettings();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update quotes source');
    } finally {
      setQuotesSaving(false);
    }
  };

  const handleDeleteQuotesSource = async (provider: string) => {
    try {
      await integrationsApi.deleteSettingsByProvider('quotes', provider);
      setActionSuccess('Quotes source removed successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      fetchIntegrationSettings();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to delete quotes source');
      setTimeout(() => setActionError(''), 3000);
    }
  };

  const getProviderName = (providerId: string, type: 'news' | 'quotes') => {
    const newsProviders: Record<string, string> = {
      'newsapi': 'NewsAPI.org',
      'gnews': 'GNews',
      'mediastack': 'Mediastack',
      'none': 'Disabled'
    };
    const quotesProviders: Record<string, string> = {
      'quotable': 'Quotable',
      'api-ninjas': 'API Ninjas',
      'zenquotes': 'ZenQuotes',
      'none': 'Disabled'
    };
    return type === 'news' ? newsProviders[providerId] || providerId : quotesProviders[providerId] || providerId;
  };

  // Auto-Waves functions
  const fetchAutoWavesData = async () => {
    setAutoWavesLoading(true);
    try {
      const [rulesResponse, logsResponse] = await Promise.all([
        autoWavesApi.getRules(true),
        autoWavesApi.getLogs({ limit: 20 })
      ]);
      setAutoWavesRules(rulesResponse.data.data || []);
      setAutoWavesLogs(logsResponse.data.data || []);
    } catch (error) {
      console.error('Failed to fetch auto-waves data:', error);
    } finally {
      setAutoWavesLoading(false);
    }
  };

  const resetAutoWavesForm = () => {
    setAutoWavesFormData({
      name: '',
      description: '',
      rule_type: 'recurring',
      frequency: 'monthly',
      day_of_week: 1,
      day_of_month: 1,
      month_of_year: 1,
      waves_amount: 100,
      target_type: 'all_active',
      target_roles: [],
      reason_template: 'Auto-credited waves',
      is_active: true
    });
    setActionError('');
  };

  const handleAddAutoWavesRule = async () => {
    if (!autoWavesFormData.name) {
      setActionError('Please enter a rule name');
      return;
    }

    if (autoWavesFormData.waves_amount <= 0) {
      setActionError('Waves amount must be greater than 0');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await autoWavesApi.createRule({
        name: autoWavesFormData.name,
        description: autoWavesFormData.description || undefined,
        rule_type: autoWavesFormData.rule_type,
        frequency: autoWavesFormData.rule_type === 'recurring' ? autoWavesFormData.frequency : undefined,
        day_of_week: autoWavesFormData.frequency === 'weekly' ? autoWavesFormData.day_of_week : undefined,
        day_of_month: ['monthly', 'quarterly', 'yearly'].includes(autoWavesFormData.frequency) ? autoWavesFormData.day_of_month : undefined,
        month_of_year: autoWavesFormData.frequency === 'yearly' ? autoWavesFormData.month_of_year : undefined,
        waves_amount: autoWavesFormData.waves_amount,
        target_type: autoWavesFormData.target_type,
        target_roles: autoWavesFormData.target_type === 'by_role' ? autoWavesFormData.target_roles : undefined,
        reason_template: autoWavesFormData.reason_template,
        is_active: autoWavesFormData.is_active
      });
      setActionSuccess('Auto-waves rule created successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowAddAutoWavesModal(false);
      resetAutoWavesForm();
      fetchAutoWavesData();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to create auto-waves rule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditAutoWavesRule = (rule: AutoWavesRule) => {
    setSelectedAutoWavesRule(rule);
    setAutoWavesFormData({
      name: rule.name,
      description: rule.description || '',
      rule_type: rule.rule_type,
      frequency: rule.frequency || 'monthly',
      day_of_week: rule.day_of_week || 1,
      day_of_month: rule.day_of_month || 1,
      month_of_year: rule.month_of_year || 1,
      waves_amount: rule.waves_amount,
      target_type: rule.target_type,
      target_roles: rule.target_roles || [],
      reason_template: rule.reason_template || 'Auto-credited waves',
      is_active: rule.is_active
    });
    setShowEditAutoWavesModal(true);
  };

  const handleUpdateAutoWavesRule = async () => {
    if (!selectedAutoWavesRule) return;

    setActionLoading(true);
    setActionError('');

    try {
      await autoWavesApi.updateRule(selectedAutoWavesRule.id, {
        name: autoWavesFormData.name,
        description: autoWavesFormData.description || undefined,
        rule_type: autoWavesFormData.rule_type,
        frequency: autoWavesFormData.rule_type === 'recurring' ? autoWavesFormData.frequency : undefined,
        day_of_week: autoWavesFormData.frequency === 'weekly' ? autoWavesFormData.day_of_week : undefined,
        day_of_month: ['monthly', 'quarterly', 'yearly'].includes(autoWavesFormData.frequency) ? autoWavesFormData.day_of_month : undefined,
        month_of_year: autoWavesFormData.frequency === 'yearly' ? autoWavesFormData.month_of_year : undefined,
        waves_amount: autoWavesFormData.waves_amount,
        target_type: autoWavesFormData.target_type,
        target_roles: autoWavesFormData.target_type === 'by_role' ? autoWavesFormData.target_roles : undefined,
        reason_template: autoWavesFormData.reason_template,
        is_active: autoWavesFormData.is_active
      });
      setActionSuccess('Auto-waves rule updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowEditAutoWavesModal(false);
      setSelectedAutoWavesRule(null);
      resetAutoWavesForm();
      fetchAutoWavesData();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update auto-waves rule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAutoWavesRule = async () => {
    if (!selectedAutoWavesRule) return;

    setActionLoading(true);
    try {
      await autoWavesApi.deleteRule(selectedAutoWavesRule.id);
      setActionSuccess('Auto-waves rule deleted successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowDeleteAutoWavesModal(false);
      setSelectedAutoWavesRule(null);
      fetchAutoWavesData();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to delete auto-waves rule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAutoWavesRule = async (rule: AutoWavesRule) => {
    try {
      await autoWavesApi.updateRule(rule.id, { is_active: !rule.is_active });
      setActionSuccess(`Rule ${!rule.is_active ? 'enabled' : 'disabled'} successfully!`);
      setTimeout(() => setActionSuccess(''), 2000);
      fetchAutoWavesData();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to toggle rule');
      setTimeout(() => setActionError(''), 3000);
    }
  };

  const handleTriggerAutoWavesRule = async (ruleId: string) => {
    setActionLoading(true);
    try {
      const response = await autoWavesApi.triggerRule(ruleId);
      setActionSuccess(`Rule executed! ${response.data.data.usersProcessed} users received ${response.data.data.totalWaves} waves`);
      setTimeout(() => setActionSuccess(''), 5000);
      fetchAutoWavesData();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to trigger rule');
      setTimeout(() => setActionError(''), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const getRuleTypeLabel = (type: string) => {
    switch (type) {
      case 'recurring': return 'Recurring';
      case 'birthday': return 'Birthday';
      case 'work_anniversary': return 'Work Anniversary';
      default: return type;
    }
  };

  const getFrequencyLabel = (frequency?: string) => {
    switch (frequency) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      case 'quarterly': return 'Quarterly';
      case 'yearly': return 'Yearly';
      default: return '-';
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const resetRippleTypeForm = () => {
    setRippleTypeFormData({
      name: '',
      key: '',
      description: '',
      icon: '👏',
      color: '#0C6B62',
      default_points: '10'
    });
    setActionError('');
  };

  const handleAddRippleType = async () => {
    if (!rippleTypeFormData.name || !rippleTypeFormData.key) {
      setActionError('Please fill in name and key');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await settingsApi.createRippleType({
        name: rippleTypeFormData.name,
        key: rippleTypeFormData.key.toLowerCase().replace(/\s+/g, '_'),
        description: rippleTypeFormData.description || undefined,
        icon: rippleTypeFormData.icon,
        color: rippleTypeFormData.color,
        default_points: parseInt(rippleTypeFormData.default_points) || 10
      });
      setActionSuccess('Ripple type created successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowAddRippleTypeModal(false);
      resetRippleTypeForm();
      fetchRippleTypes();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to create ripple type');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditRippleType = (rippleType: RippleType) => {
    setSelectedRippleType(rippleType);
    setRippleTypeFormData({
      name: rippleType.name,
      key: rippleType.key,
      description: rippleType.description || '',
      icon: rippleType.icon || '👏',
      color: rippleType.color || '#0C6B62',
      default_points: rippleType.default_points?.toString() || '10'
    });
    setActionError('');
    setShowEditRippleTypeModal(true);
  };

  const handleUpdateRippleType = async () => {
    if (!selectedRippleType) return;

    if (!rippleTypeFormData.name || !rippleTypeFormData.key) {
      setActionError('Please fill in name and key');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await settingsApi.updateRippleType(selectedRippleType.id, {
        name: rippleTypeFormData.name,
        key: rippleTypeFormData.key.toLowerCase().replace(/\s+/g, '_'),
        description: rippleTypeFormData.description || undefined,
        icon: rippleTypeFormData.icon,
        color: rippleTypeFormData.color,
        default_points: parseInt(rippleTypeFormData.default_points) || 10
      });
      setActionSuccess('Ripple type updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowEditRippleTypeModal(false);
      resetRippleTypeForm();
      setSelectedRippleType(null);
      fetchRippleTypes();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update ripple type');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRippleType = async () => {
    if (!selectedRippleType) return;

    setActionLoading(true);
    setActionError('');

    try {
      await settingsApi.deleteRippleType(selectedRippleType.id);
      setActionSuccess('Ripple type deleted successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowDeleteRippleTypeModal(false);
      setSelectedRippleType(null);
      fetchRippleTypes();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to delete ripple type');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleRippleTypeActive = async (rippleType: RippleType) => {
    try {
      await settingsApi.updateRippleType(rippleType.id, {
        is_active: !rippleType.is_active
      });
      setActionSuccess(`Ripple type ${rippleType.is_active ? 'deactivated' : 'activated'} successfully!`);
      setTimeout(() => setActionSuccess(''), 3000);
      fetchRippleTypes();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update ripple type');
    }
  };

  const handlePermissionChange = async (featureId: string, role: string, permission: string, value: boolean) => {
    const key = `${featureId}-${role}`;
    const currentPerms = permissionsMatrix.find(p => p.feature_id === featureId)?.permissions[role] || {
      can_view: true,
      can_create: false,
      can_edit: false,
      can_delete: false
    };

    try {
      await settingsApi.updateRolePermission({
        feature_id: featureId,
        role,
        can_view: permission === 'can_view' ? value : currentPerms.can_view,
        can_create: permission === 'can_create' ? value : currentPerms.can_create,
        can_edit: permission === 'can_edit' ? value : currentPerms.can_edit,
        can_delete: permission === 'can_delete' ? value : currentPerms.can_delete,
      });

      // Update local state
      setPermissionsMatrix(prev => prev.map(p => {
        if (p.feature_id === featureId) {
          return {
            ...p,
            permissions: {
              ...p.permissions,
              [role]: {
                ...p.permissions[role],
                [permission]: value
              }
            }
          };
        }
        return p;
      }));

      setActionSuccess('Permission updated!');
      setTimeout(() => setActionSuccess(''), 2000);
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update permission');
      setTimeout(() => setActionError(''), 3000);
    }
  };

  const tabs = [
    { id: 'users' as TabType, label: 'User Management', icon: Users },
    ...(currentUser?.role === 'superadmin' ? [{ id: 'points' as TabType, label: 'Waves Management', icon: Coins }] : []),
    ...(currentUser?.role === 'superadmin' ? [{ id: 'rewards' as TabType, label: 'Rewards Management', icon: Gift }] : []),
    { id: 'values' as TabType, label: 'Company Values', icon: Award },
    ...(currentUser?.role === 'superadmin' ? [{ id: 'ripple-types' as TabType, label: 'Ripple Types', icon: Heart }] : []),
    ...(currentUser?.role === 'superadmin' ? [{ id: 'permissions' as TabType, label: 'Role Permissions', icon: UserCog }] : []),
    ...(currentUser?.role === 'superadmin' ? [{ id: 'integrations' as TabType, label: 'Integrations', icon: Plug }] : []),
    ...((currentUser?.role === 'superadmin' || currentUser?.role === 'admin') ? [{ id: 'events' as TabType, label: 'Events Management', icon: Calendar }] : []),
    ...(currentUser?.role === 'superadmin' ? [{ id: 'security' as TabType, label: 'Security', icon: Shield }] : []),
  ];

  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch = searchTerm === '' ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    // Role filter (handle both 'mentor' and 'manager' as the same)
    const matchesRole = roleFilter === '' ||
      user.role === roleFilter ||
      (roleFilter === 'mentor' && user.role === 'manager');

    // Status filter
    const matchesStatus = statusFilter === '' ||
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'superadmin': return 'danger';
      case 'admin': return 'danger';
      case 'executive': return 'primary';
      case 'mentor':
      case 'manager': return 'warning';
      default: return 'default';
    }
  };

  const resetAddUserForm = () => {
    setAddUserFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      role: 'associate',
      mentor_id: ''
    });
    setActionError('');
  };

  const handleAddUser = async () => {
    // Validate required fields
    if (!addUserFormData.first_name || !addUserFormData.last_name || !addUserFormData.email || !addUserFormData.phone || !addUserFormData.password) {
      setActionError('Please fill in all required fields');
      return;
    }

    if (addUserFormData.password.length < 8) {
      setActionError('Password must be at least 8 characters');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await usersApi.create(addUserFormData);
      setActionSuccess('User created successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowAddUserModal(false);
      resetAddUserForm();
      fetchUsers();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone || '',
      role: user.role === 'manager' ? 'mentor' : user.role,
      mentor_id: user.mentor_id || '',
      is_active: user.is_active,
      birthday: user.birthday ? user.birthday.split('T')[0] : '',
      joining_date: user.joining_date ? user.joining_date.split('T')[0] : '',
    });
    setActionError('');
    setShowEditUserModal(true);
  };

  const [deleteOptions, setDeleteOptions] = useState({
    deleteType: 'deactivate' as 'deactivate' | 'permanent',
    removeContributions: false
  });

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setActionError('');
    setDeleteOptions({ deleteType: 'deactivate', removeContributions: false });
    setShowDeleteConfirmModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    setActionError('');

    try {
      // Update basic user info
      await usersApi.update(selectedUser.id, {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        email: editFormData.email,
        phone: editFormData.phone,
        role: editFormData.role,
        mentor_id: editFormData.mentor_id,
        is_active: editFormData.is_active,
      });

      // Update birthday and joining date if superadmin
      if (currentUser?.role === 'superadmin') {
        await eventsApi.updateUserDates(selectedUser.id, {
          birthday: editFormData.birthday || undefined,
          joining_date: editFormData.joining_date || undefined,
        });
      }

      setActionSuccess('User updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowEditUserModal(false);
      fetchUsers();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    setActionError('');

    try {
      const options = deleteOptions.deleteType === 'permanent'
        ? { permanent: true, removeContributions: deleteOptions.removeContributions }
        : undefined;

      await usersApi.delete(selectedUser.id, options);

      const successMessage = deleteOptions.deleteType === 'permanent'
        ? deleteOptions.removeContributions
          ? 'User and all contributions permanently deleted!'
          : 'User permanently deleted, contributions preserved!'
        : 'User deactivated successfully!';

      setActionSuccess(successMessage);
      setTimeout(() => setActionSuccess(''), 3000);
      setShowDeleteConfirmModal(false);
      fetchUsers();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleNotification = (key: keyof typeof notificationSettings) => {
    if (typeof notificationSettings[key] === 'boolean') {
      setNotificationSettings(prev => ({
        ...prev,
        [key]: !prev[key]
      }));
    }
  };

  const handleManagePoints = async () => {
    if (!managePointsData.user_id || !managePointsData.amount) {
      setActionError('Please select a user and enter an amount');
      return;
    }

    const amount = parseInt(managePointsData.amount);
    if (isNaN(amount) || amount <= 0) {
      setActionError('Please enter a valid positive amount');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await usersApi.managePoints({
        user_id: managePointsData.user_id,
        amount,
        operation: managePointsData.operation,
        reason: managePointsData.reason || undefined
      });

      const successMessage = managePointsData.operation === 'add'
        ? 'Waves added successfully!'
        : 'Waves reduced successfully!';
      setActionSuccess(successMessage);
      setTimeout(() => setActionSuccess(''), 3000);
      setShowManagePointsModal(false);
      setManagePointsData({ user_id: '', amount: '', operation: 'add', reason: '' });
      fetchUsers(); // Refresh user list to show updated balances
      refreshUser(); // Refresh current user's data (superadmin balance may have changed)
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to manage waves');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    if (!resetPasswordData.new_password || !resetPasswordData.confirm_password) {
      setActionError('Please fill in both password fields');
      return;
    }

    if (resetPasswordData.new_password.length < 8) {
      setActionError('Password must be at least 8 characters');
      return;
    }

    if (resetPasswordData.new_password !== resetPasswordData.confirm_password) {
      setActionError('Passwords do not match');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await usersApi.resetPassword(selectedUser.id, resetPasswordData.new_password);
      setActionSuccess(`Password reset successfully for ${selectedUser.first_name} ${selectedUser.last_name}!`);
      setTimeout(() => setActionSuccess(''), 3000);
      setShowResetPasswordModal(false);
      setResetPasswordData({ new_password: '', confirm_password: '' });
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredPointsUsers = users.filter(u =>
    u.is_active && (
      pointsSearchTerm === '' ||
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(pointsSearchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(pointsSearchTerm.toLowerCase())
    )
  );

  const renderUserManagement = () => (
    <div className={styles.tabContent}>
      <div className={styles.contentHeader}>
        <div>
          <h2>User Management</h2>
          <p>Manage user accounts, roles, and permissions</p>
        </div>
        <Button onClick={() => setShowAddUserModal(true)}>
          <UserPlus size={18} /> Add User
        </Button>
      </div>

      <Card>
        <CardContent>
          <div className={styles.tableControls}>
            <div className={styles.searchBox}>
              <Search size={18} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles.filterGroup}>
              <select
                className={styles.select}
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="superadmin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="executive">Executive</option>
                <option value="mentor">Mentor</option>
                <option value="associate">Associate</option>
              </select>
              <select
                className={styles.select}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading users...</p>
            </div>
          ) : (
            <div className={styles.userTable}>
              <div className={styles.tableHeader}>
                <span>User</span>
                <span>Mentor</span>
                <span>Role</span>
                <span>Birthday</span>
                <span>Joined</span>
                <span>Waves</span>
                <span>Status</span>
                <span></span>
              </div>
              {filteredUsers.map((u) => (
                <div key={u.id} className={styles.tableRow}>
                  <div className={styles.userCell}>
                    <Avatar
                      src={u.avatar_url}
                      name={`${u.first_name} ${u.last_name}`}
                      size="sm"
                    />
                    <div className={styles.userInfo}>
                      <span className={styles.userName}>
                        {u.first_name} {u.last_name}
                      </span>
                      <span className={styles.userEmail}>{u.email}</span>
                    </div>
                  </div>
                  <span className={styles.cellText}>
                    {getMentorDisplay(u)}
                  </span>
                  <Badge variant={getRoleBadgeVariant(u.role)}>
                    {formatRole(u.role)}
                  </Badge>
                  <span className={styles.cellText}>
                    {u.birthday ? new Date(u.birthday).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </span>
                  <span className={styles.cellText}>
                    {u.joining_date ? new Date(u.joining_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </span>
                  <span className={styles.wavesBalance}>
                    <Coins size={14} /> {u.points_balance || 0}
                  </span>
                  <Badge variant={u.is_active ? 'success' : 'default'}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <div className={styles.actionMenuWrapper} ref={openActionMenu === u.id ? actionMenuRef : null}>
                    <button
                      className={styles.actionMenuBtn}
                      onClick={(e) => {
                        if (openActionMenu === u.id) {
                          setOpenActionMenu(null);
                          return;
                        }
                        // Fixed positioning so the menu escapes the table's scroll container
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        const menuW = 190;
                        const menuH = 200;
                        const top = rect.bottom + menuH + 4 <= window.innerHeight
                          ? rect.bottom + 4
                          : Math.max(8, rect.top - menuH - 4);
                        const left = Math.max(8, Math.min(rect.right - menuW, window.innerWidth - menuW - 8));
                        setActionMenuPos({ top, left });
                        setOpenActionMenu(u.id);
                      }}
                    >
                      <MoreVertical size={18} />
                    </button>
                    {openActionMenu === u.id && (
                      <div
                        className={styles.actionDropdown}
                        style={actionMenuPos ? { position: 'fixed', top: actionMenuPos.top, left: actionMenuPos.left, right: 'auto', bottom: 'auto' } : undefined}
                      >
                        <button
                          className={styles.dropdownItem}
                          onClick={() => {
                            handleEditUser(u);
                            setOpenActionMenu(null);
                          }}
                        >
                          <Edit2 size={14} />
                          <span>Edit User</span>
                        </button>
                        <button
                          className={styles.dropdownItem}
                          onClick={() => {
                            setSelectedUser(u);
                            setResetPasswordData({ new_password: '', confirm_password: '' });
                            setActionError('');
                            setShowResetPasswordModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          <Key size={14} />
                          <span>Reset Password</span>
                        </button>
                        <button
                          className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                          onClick={() => {
                            handleDeleteClick(u);
                            setOpenActionMenu(null);
                          }}
                        >
                          <Trash2 size={14} />
                          <span>Delete User</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderPoints = () => (
    <div className={styles.tabContent}>
      <div className={styles.contentHeader}>
        <div>
          <h2>Waves Management</h2>
          <p>Manage waves and configure automatic distributions</p>
        </div>
        <div className={styles.headerActions}>
          <Button onClick={() => setShowManagePointsModal(true)}>
            <Coins size={18} /> Manage Waves
          </Button>
          <Button variant="outline" onClick={() => setShowAddAutoWavesModal(true)}>
            <Plus size={18} /> Add Auto Rule
          </Button>
        </div>
      </div>

      {autoWavesLoading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>
      ) : autoWavesRules.length === 0 ? (
        <Card>
          <CardContent>
            <div className={styles.emptyState}>
              <RefreshCw size={48} />
              <h3>No auto-waves rules configured</h3>
              <p>Create rules to automatically distribute waves on birthdays, anniversaries, or schedules</p>
              <Button onClick={() => setShowAddAutoWavesModal(true)}>
                <Plus size={18} /> Add Auto Rule
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className={styles.wavesGrid}>
          {autoWavesRules.map((rule) => (
            <Card key={rule.id} className={`${styles.waveRuleCard} ${!rule.is_active ? styles.inactive : ''}`}>
              <CardContent>
                <div className={styles.waveRuleHeader}>
                  <div className={styles.waveRuleIcon}>
                    {rule.rule_type === 'birthday' ? <Cake size={32} /> :
                     rule.rule_type === 'work_anniversary' ? <Briefcase size={32} /> :
                     <RefreshCw size={32} />}
                  </div>
                  <Badge variant={rule.is_active ? 'success' : 'default'}>
                    {rule.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className={styles.waveRuleContent}>
                  <h3 className={styles.waveRuleName}>{rule.name}</h3>
                  <div className={styles.waveRuleBadges}>
                    <Badge variant={rule.rule_type === 'recurring' ? 'primary' : rule.rule_type === 'birthday' ? 'warning' : 'success'}>
                      {getRuleTypeLabel(rule.rule_type)}
                    </Badge>
                    {rule.rule_type === 'recurring' && (
                      <Badge variant="default">{getFrequencyLabel(rule.frequency)}</Badge>
                    )}
                  </div>
                  {rule.description && <p className={styles.waveRuleDescription}>{rule.description}</p>}
                  <div className={styles.waveRuleMeta}>
                    <span className={styles.waveAmount}>
                      <Star size={16} />
                      {rule.waves_amount} waves
                    </span>
                    <span className={styles.waveTarget}>
                      <Users size={16} />
                      {rule.target_type === 'all_active' ? 'All users' :
                       rule.target_type === 'by_role' ? 'By role' : 'Specific users'}
                    </span>
                  </div>
                  <div className={styles.waveRuleStats}>
                    <span><Clock size={14} /> Executed {rule.execution_count} times</span>
                  </div>
                  <div className={styles.waveRuleActions}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleAutoWavesRule(rule)}
                    >
                      {rule.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      {rule.is_active ? 'Disable' : 'Enable'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEditAutoWavesRule(rule)}>
                      <Edit2 size={14} /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedAutoWavesRule(rule);
                        setShowDeleteAutoWavesModal(true);
                      }}
                    >
                      <Trash2 size={14} /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Execution Logs */}
      {autoWavesLogs.length > 0 && (
        <Card className={styles.logsCard}>
          <CardContent>
            <h3 className={styles.sectionTitle}>
              <Clock size={20} /> Recent Execution Logs
            </h3>
            <div className={styles.logsList}>
              <table className={styles.logsTable}>
                <thead>
                  <tr>
                    <th>Rule</th>
                    <th>Type</th>
                    <th>Associate</th>
                    <th>Executed At</th>
                    <th>Users</th>
                    <th>Waves</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {autoWavesLogs.slice(0, 5).map((log) => (
                    <tr key={log.id}>
                      <td>{log.rule_name}</td>
                      <td>{getRuleTypeLabel(log.rule_type)}</td>
                      <td>{log.user_first_name ? `${log.user_first_name} ${log.user_last_name}` : '—'}</td>
                      <td>{formatDateTime(log.executed_at)}</td>
                      <td>{log.users_processed}</td>
                      <td>{log.total_waves_distributed}</td>
                      <td>
                        <Badge variant={log.status === 'success' ? 'success' : log.status === 'partial' ? 'warning' : 'danger'}>
                          {log.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderCompanyValues = () => (
    <div className={styles.tabContent}>
      <div className={styles.contentHeader}>
        <div>
          <h2>Company Values</h2>
          <p>Define the core values that guide your organization's culture</p>
        </div>
        <Button onClick={() => { resetValueForm(); setShowAddValueModal(true); }}>
          <Plus size={18} /> Add Value
        </Button>
      </div>

      <div className={styles.valuesGrid}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading values...</p>
          </div>
        ) : companyValues.length === 0 ? (
          <Card>
            <CardContent>
              <div className={styles.emptyState}>
                <Award size={48} />
                <h3>No company values defined</h3>
                <p>Add your first company value to get started</p>
                <Button onClick={() => { resetValueForm(); setShowAddValueModal(true); }}>
                  <Plus size={18} /> Add Value
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          companyValues.map((value) => (
            <Card key={value.id} className={styles.valueCard}>
              <CardContent>
                <div className={styles.valueHeader}>
                  {value.icon_url ? (
                    <div
                      className={styles.valueIconImage}
                      style={{ background: value.color + '20' }}
                    >
                      <img src={value.icon_url} alt={value.name} />
                    </div>
                  ) : (
                    <div
                      className={styles.valueIcon}
                      style={{ background: value.color + '20', color: value.color }}
                    >
                      {renderValueIcon(value.icon, 24, value.color)}
                    </div>
                  )}
                  <div className={styles.valueActions}>
                    <button
                      className={styles.actionBtn}
                      title="Edit"
                      onClick={() => handleEditValue(value)}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className={styles.actionBtn}
                      title="Delete"
                      onClick={() => { setSelectedValue(value); setShowDeleteValueModal(true); }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <h3 className={styles.valueName}>{value.name}</h3>
                <p className={styles.valueDescription}>{value.description}</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  // === Events Management Functions ===

  // iconOptions and getEventIconComponent are now imported from utils/eventIcons

  const fetchEventsData = async () => {
    setEventsLoading(true);
    try {
      const [eventsRes, templatesRes, announcementTemplatesRes, eventTypesRes] = await Promise.allSettled([
        eventsApi.getAll({ include_auto: false }),
        eventsApi.getTemplates(),
        eventsApi.getAnnouncementTemplates(),
        eventsApi.getEventTypes(true),
      ]);
      if (eventsRes.status === 'fulfilled') setCustomEvents(eventsRes.value.data.events || []);
      if (templatesRes.status === 'fulfilled') setEventTemplates(templatesRes.value.data.templates || []);
      if (announcementTemplatesRes.status === 'fulfilled') setAnnouncementTemplates(announcementTemplatesRes.value.data.templates || []);
      if (eventTypesRes.status === 'fulfilled') setEventTypes(eventTypesRes.value.data.eventTypes || []);
    } catch (error) {
      console.error('Error fetching events data:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  // Targeted refresh functions — only reload what changed
  const refreshEvents = async () => {
    try {
      const res = await eventsApi.getAll({ include_auto: false });
      setCustomEvents(res.data.events || []);
    } catch (error) { console.error('Error refreshing events:', error); }
  };

  const refreshEventTemplates = async () => {
    try {
      const res = await eventsApi.getTemplates();
      setEventTemplates(res.data.templates || []);
    } catch (error) { console.error('Error refreshing event templates:', error); }
  };

  const refreshAnnouncementTemplates = async () => {
    try {
      const res = await eventsApi.getAnnouncementTemplates();
      setAnnouncementTemplates(res.data.templates || []);
    } catch (error) { console.error('Error refreshing announcement templates:', error); }
  };

  const refreshEventTypes = async () => {
    try {
      const res = await eventsApi.getEventTypes(true);
      setEventTypes(res.data.eventTypes || []);
    } catch (error) { console.error('Error refreshing event types:', error); }
  };

  const getDefaultEventType = () => {
    const available = eventTypes.filter(et => !et.is_system && et.is_active);
    return available.length > 0 ? available[0].key : 'custom';
  };

  const resetEventForm = () => {
    setEventFormData({
      title: '',
      description: '',
      event_date: '',
      event_type: getDefaultEventType(),
      image_url: '',
      link_url: '',
      link_title: '',
      is_recurring: false,
      recurring_frequency: 'annually' as RecurringFrequency,
      recurring_day_of_week: 0,
      recurring_week_of_month: 1,
      start_time: '',
      end_time: '',
    });
    setActionError('');
  };

  const handleCreateEvent = async () => {
    if (!eventFormData.title || !eventFormData.event_date) {
      setActionError('Title and event date are required');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      await eventsApi.create({
        title: eventFormData.title,
        event_date: eventFormData.event_date,
        event_type: eventFormData.event_type || getDefaultEventType(),
        description: eventFormData.description || undefined,
        image_url: eventFormData.image_url || undefined,
        link_url: eventFormData.link_url || undefined,
        link_title: eventFormData.link_title || undefined,
        is_recurring: eventFormData.is_recurring,
        recurring_frequency: eventFormData.is_recurring ? eventFormData.recurring_frequency : undefined,
        recurring_day_of_week: eventFormData.is_recurring && eventFormData.recurring_frequency === 'monthly' ? eventFormData.recurring_day_of_week : undefined,
        recurring_week_of_month: eventFormData.is_recurring && eventFormData.recurring_frequency === 'monthly' ? eventFormData.recurring_week_of_month : undefined,
        start_time: eventFormData.start_time || undefined,
        end_time: eventFormData.end_time || undefined,
      });
      setActionSuccess('Event created successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowCreateEventModal(false);
      resetEventForm();
      refreshEvents();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to create event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setEventFormData({
      title: event.title,
      description: event.description || '',
      event_date: event.event_date ? event.event_date.split('T')[0] : '',
      event_type: event.event_type || 'custom',
      image_url: event.image_url || '',
      link_url: event.link_url || '',
      link_title: event.link_title || '',
      is_recurring: event.is_recurring || false,
      recurring_frequency: event.recurring_frequency || 'annually',
      recurring_day_of_week: event.recurring_day_of_week || 0,
      recurring_week_of_month: event.recurring_week_of_month || 1,
      start_time: event.start_time || '',
      end_time: event.end_time || '',
    });
    setActionError('');
    setShowEditEventModal(true);
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    if (!eventFormData.title || !eventFormData.event_date) {
      setActionError('Title and event date are required');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      await eventsApi.update(selectedEvent.id, {
        title: eventFormData.title,
        event_date: eventFormData.event_date,
        event_type: eventFormData.event_type || getDefaultEventType(),
        description: eventFormData.description || undefined,
        image_url: eventFormData.image_url || undefined,
        link_url: eventFormData.link_url || undefined,
        link_title: eventFormData.link_title || undefined,
        is_recurring: eventFormData.is_recurring,
        recurring_frequency: eventFormData.is_recurring ? eventFormData.recurring_frequency : undefined,
        recurring_day_of_week: eventFormData.is_recurring && eventFormData.recurring_frequency === 'monthly' ? eventFormData.recurring_day_of_week : undefined,
        recurring_week_of_month: eventFormData.is_recurring && eventFormData.recurring_frequency === 'monthly' ? eventFormData.recurring_week_of_month : undefined,
        start_time: eventFormData.start_time || undefined,
        end_time: eventFormData.end_time || undefined,
      });
      setActionSuccess('Event updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowEditEventModal(false);
      resetEventForm();
      refreshEvents();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    setActionLoading(true);
    try {
      await eventsApi.delete(selectedEvent.id);
      setActionSuccess('Event deleted successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowDeleteEventModal(false);
      setSelectedEvent(null);
      refreshEvents();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to delete event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEventImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setActionError('Image must be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setEventFormData({ ...eventFormData, image_url: base64 });
    };
    reader.readAsDataURL(file);
  };

  const handleEventTemplateEdit = (template: EventTemplate) => {
    setEditingEventTemplateId(template.id);
    setEventTemplateFormData({
      title_template: template.title_template,
      message_template: template.message_template,
      icon_name: template.icon_name || 'cake',
      image_url: template.image_url || '',
    });
  };

  const handleEventTemplateSave = async (templateId: string) => {
    setActionLoading(true);
    try {
      await eventsApi.updateTemplate(templateId, {
        title_template: eventTemplateFormData.title_template,
        message_template: eventTemplateFormData.message_template,
        image_url: eventTemplateFormData.image_url || undefined,
      });
      setActionSuccess('Template updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setEditingEventTemplateId(null);
      refreshEventTemplates();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEventTemplateImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setActionError('Image must be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setEventTemplateFormData({ ...eventTemplateFormData, image_url: base64 });
    };
    reader.readAsDataURL(file);
  };

  const handleAnnouncementTemplateEdit = (template: AnnouncementTemplate) => {
    setEditingAnnouncementTemplateId(template.id);
    setAnnouncementTemplateFormData({
      title_template: template.title_template,
      message_template: template.message_template,
      image_url: template.image_url || '',
      auto_post_enabled: template.auto_post_enabled,
      post_timing_days_before: template.post_timing_days_before,
    });
  };

  const handleAnnouncementTemplateSave = async (templateId: string) => {
    setActionLoading(true);
    try {
      await eventsApi.updateAnnouncementTemplate(templateId, {
        title_template: announcementTemplateFormData.title_template,
        message_template: announcementTemplateFormData.message_template,
        image_url: announcementTemplateFormData.image_url || undefined,
        auto_post_enabled: announcementTemplateFormData.auto_post_enabled,
        post_timing_days_before: announcementTemplateFormData.post_timing_days_before,
      });
      setActionSuccess('Announcement template updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setEditingAnnouncementTemplateId(null);
      refreshAnnouncementTemplates();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update announcement template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAnnouncementToggle = async (template: AnnouncementTemplate) => {
    try {
      await eventsApi.updateAnnouncementTemplate(template.id, {
        auto_post_enabled: !template.auto_post_enabled,
      });
      refreshAnnouncementTemplates();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update');
    }
  };

  const handleAnnouncementTemplateImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setActionError('Image must be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setAnnouncementTemplateFormData({ ...announcementTemplateFormData, image_url: base64 });
    };
    reader.readAsDataURL(file);
  };

  // Helper: get event types without an event display template
  const getAvailableTypesForEventTemplate = () => {
    const usedTypes = eventTemplates.map(t => t.template_type);
    return eventTypes.filter(et => et.is_active && !usedTypes.includes(et.key));
  };

  // Helper: get event types without an announcement template
  const getAvailableTypesForAnnouncementTemplate = () => {
    const usedTypes = announcementTemplates.map(t => t.template_type);
    return eventTypes.filter(et => et.is_active && !usedTypes.includes(et.key));
  };

  const handleCreateEventTemplate = async () => {
    try {
      setActionLoading(true);
      await eventsApi.createTemplate(createEventTemplateFormData);
      setShowCreateEventTemplateModal(false);
      setCreateEventTemplateFormData({ template_type: '', title_template: '', message_template: '', icon_name: 'party', image_url: '' });
      setActionSuccess('Event display template created successfully!');
      refreshEventTemplates();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create event display template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEventTemplate = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this event display template?')) return;
    try {
      setActionLoading(true);
      await eventsApi.deleteTemplate(id);
      setActionSuccess('Event display template deleted successfully!');
      refreshEventTemplates();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete event display template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateAnnouncementTemplate = async () => {
    try {
      setActionLoading(true);
      await eventsApi.createAnnouncementTemplate(createAnnouncementTemplateFormData);
      setShowCreateAnnouncementTemplateModal(false);
      setCreateAnnouncementTemplateFormData({ template_type: '', title_template: '', message_template: '', image_url: '', auto_post_enabled: false, post_timing_days_before: 0 });
      setActionSuccess('Announcement template created successfully!');
      refreshAnnouncementTemplates();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create announcement template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAnnouncementTemplate = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this announcement template?')) return;
    try {
      setActionLoading(true);
      await eventsApi.deleteAnnouncementTemplate(id);
      setActionSuccess('Announcement template deleted successfully!');
      refreshAnnouncementTemplates();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete announcement template');
    } finally {
      setActionLoading(false);
    }
  };

  // === Event Type CRUD handlers ===
  const handleCreateEventType = async () => {
    try {
      await eventsApi.createEventType(eventTypeFormData);
      setShowEventTypeModal(false);
      setEventTypeFormData({ name: '', key: '', description: '', icon_name: 'party', color: '#6366F1' });
      refreshEventTypes();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create event type');
    }
  };

  const handleUpdateEventType = async () => {
    if (!editingEventType) return;
    try {
      await eventsApi.updateEventType(editingEventType.id, eventTypeFormData);
      setEditingEventType(null);
      setShowEventTypeModal(false);
      refreshEventTypes();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update event type');
    }
  };

  const handleDeleteEventType = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this event type?')) return;
    try {
      await eventsApi.deleteEventType(id);
      refreshEventTypes();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete event type');
    }
  };

  const handleToggleEventTypeActive = async (et: EventTypeConfig) => {
    try {
      await eventsApi.updateEventType(et.id, { is_active: !et.is_active });
      refreshEventTypes();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update event type');
    }
  };

  const renderEventForm = (_isEdit: boolean) => (
    <div className={styles.evtForm}>
      <div>
        <label className={styles.evtFieldLabel}>Event Type <span style={{ color: '#DC2626' }}>*</span></label>
        <select
          className={styles.evtFieldSelect}
          value={eventFormData.event_type || getDefaultEventType()}
          onChange={(e) => setEventFormData({ ...eventFormData, event_type: e.target.value })}
        >
          {eventTypes.filter(et => !et.is_system && et.is_active).map(et => (
            <option key={et.key} value={et.key}>{et.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className={styles.evtFieldLabel}>Title <span style={{ color: '#DC2626' }}>*</span></label>
        <input
          type="text"
          className={styles.evtFieldInput}
          placeholder="What's the event called?"
          value={eventFormData.title}
          onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
        />
      </div>

      <div>
        <label className={styles.evtFieldLabel}>
          Description <span className={styles.evtFieldOptional}>Optional</span>
        </label>
        <textarea
          className={styles.evtFieldTextarea}
          placeholder="Add details about the event…"
          rows={3}
          value={eventFormData.description}
          onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })}
        />
      </div>

      <div className={styles.evtFormRow}>
        <div>
          <label className={styles.evtFieldLabel}>Event Date <span style={{ color: '#DC2626' }}>*</span></label>
          <input
            type="date"
            className={styles.evtFieldInput}
            value={eventFormData.event_date}
            onChange={(e) => setEventFormData({ ...eventFormData, event_date: e.target.value })}
          />
        </div>
        <div>
          <label className={styles.evtFieldLabel}>
            Start Time <span className={styles.evtFieldOptional}>Optional</span>
          </label>
          <input
            type="time"
            className={styles.evtFieldInput}
            value={eventFormData.start_time}
            onChange={(e) => setEventFormData({ ...eventFormData, start_time: e.target.value })}
          />
        </div>
      </div>

      {eventFormData.start_time && (
        <div>
          <label className={styles.evtFieldLabel}>
            End Time <span className={styles.evtFieldOptional}>Optional</span>
          </label>
          <input
            type="time"
            className={styles.evtFieldInput}
            style={{ maxWidth: 240 }}
            value={eventFormData.end_time}
            onChange={(e) => setEventFormData({ ...eventFormData, end_time: e.target.value })}
          />
        </div>
      )}

      <div className={styles.evtFormRow}>
        <div>
          <label className={styles.evtFieldLabel}>
            Link URL <span className={styles.evtFieldOptional}>Optional</span>
          </label>
          <input
            type="text"
            className={styles.evtFieldInput}
            placeholder="https://example.com"
            value={eventFormData.link_url}
            onChange={(e) => setEventFormData({ ...eventFormData, link_url: e.target.value })}
          />
        </div>
        {eventFormData.link_url && (
          <div>
            <label className={styles.evtFieldLabel}>Link Title</label>
            <input
              type="text"
              className={styles.evtFieldInput}
              placeholder="Link display text"
              value={eventFormData.link_title}
              onChange={(e) => setEventFormData({ ...eventFormData, link_title: e.target.value })}
            />
          </div>
        )}
      </div>

      <label className={styles.evtCheckboxRow}>
        <input
          type="checkbox"
          checked={eventFormData.is_recurring}
          onChange={(e) => setEventFormData({ ...eventFormData, is_recurring: e.target.checked })}
        />
        <span className={styles.evtCheckboxLabel}>Make this a recurring event</span>
      </label>

      {eventFormData.is_recurring && (
        <div>
          <label className={styles.evtFieldLabel}>Frequency</label>
          <select
            className={styles.evtFieldSelect}
            value={eventFormData.recurring_frequency}
            onChange={(e) => setEventFormData({ ...eventFormData, recurring_frequency: e.target.value as RecurringFrequency })}
          >
            <option value="weekly">Weekly</option>
            <option value="fortnightly">Fortnightly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
      )}

      <div>
        <label className={styles.evtFieldLabel}>
          Event Image <span className={styles.evtFieldOptional}>Optional</span>
        </label>
        <div className={styles.evtImageUploadArea} onClick={() => eventImageInputRef.current?.click()}>
          {eventFormData.image_url ? (
            <div className={styles.evtImagePreview}>
              <img src={eventFormData.image_url} alt="Preview" />
              <button
                type="button"
                className={styles.evtRemoveImageBtn}
                onClick={(e) => { e.stopPropagation(); setEventFormData({ ...eventFormData, image_url: '' }); }}
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className={styles.evtUploadPlaceholder}>
              <Upload size={28} />
              <span>Click to upload image</span>
              <span className={styles.evtUploadHint}>Max 5MB · JPG/PNG</span>
            </div>
          )}
        </div>
        <input
          ref={eventImageInputRef}
          type="file"
          accept="image/*"
          onChange={handleEventImageUpload}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );

  const renderEventTypesSection = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <p style={{ color: '#64748B', margin: 0 }}>Define and manage event types that categorize events on the calendar</p>
        <Button onClick={() => { setEditingEventType(null); setEventTypeFormData({ name: '', key: '', description: '', icon_name: 'party', color: '#6366F1' }); setShowEventTypeModal(true); }}>
          <Plus size={16} /> Add Event Type
        </Button>
      </div>

      {eventsLoading ? (
        <div className={styles.loadingState}>Loading event types...</div>
      ) : eventTypes.length === 0 ? (
        <div className={styles.emptyState}>
          <Calendar size={48} />
          <h3>No event types</h3>
          <p>Create your first event type to get started</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {eventTypes.map(et => {
            const IconComponent = getEventIconComponent(et.icon_name);
            return (
              <div key={et.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: `${et.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IconComponent size={22} color={et.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    <span style={{ fontWeight: 600, fontSize: '15px' }}>{et.name}</span>
                    <span style={{ fontSize: '12px', color: '#94A3B8', fontFamily: 'monospace' }}>{et.key}</span>
                    {et.is_system && (
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: '#EEF2FF', color: '#6366F1', fontWeight: 500 }}>System</span>
                    )}
                    {!et.is_active && (
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: '#FEF2F2', color: '#EF4444', fontWeight: 500 }}>Inactive</span>
                    )}
                  </div>
                  {et.description && <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>{et.description}</p>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: et.color, border: '2px solid white', boxShadow: '0 0 0 1px #E2E8F0' }} />
                  <button
                    onClick={() => handleToggleEventTypeActive(et)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    {et.is_active ? (
                      <ToggleRight size={28} color="#0C6B62" />
                    ) : (
                      <ToggleLeft size={28} color="#94A3B8" />
                    )}
                  </button>
                  <Button variant="outline" size="sm" onClick={() => {
                    setEditingEventType(et);
                    setEventTypeFormData({ name: et.name, key: et.key, description: et.description || '', icon_name: et.icon_name, color: et.color });
                    setShowEventTypeModal(true);
                  }}>
                    <Edit2 size={14} />
                  </Button>
                  {!et.is_system && (
                    <Button variant="outline" size="sm" onClick={() => handleDeleteEventType(et.id)}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Event Type Create/Edit Modal */}
      <FormModal
        isOpen={showEventTypeModal}
        onClose={() => { setShowEventTypeModal(false); setEditingEventType(null); }}
        title={editingEventType ? 'Edit Event Type' : 'Add Event Type'}
        subtitle="Categorize events with a name, icon, and color"
        icon={editingEventType ? <Edit2 size={16} /> : <PartyPopper size={16} />}
        onSubmit={editingEventType ? handleUpdateEventType : handleCreateEventType}
        submitLabel={editingEventType ? 'Save Changes' : 'Create Event Type'}
        submitLoadingLabel="Saving…"
        submitLoading={actionLoading}
      >
        <div className={styles.formGroup}>
          <label>Name *</label>
          <input
            type="text"
            placeholder="e.g. Team Outing"
            value={eventTypeFormData.name}
            onChange={(e) => {
              const name = e.target.value;
              const key = editingEventType ? eventTypeFormData.key : name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
              setEventTypeFormData({ ...eventTypeFormData, name, key });
            }}
          />
        </div>
        <div className={styles.formGroup}>
          <label>Key</label>
          <input
            type="text"
            placeholder="auto-generated"
            value={eventTypeFormData.key}
            onChange={(e) => setEventTypeFormData({ ...eventTypeFormData, key: e.target.value })}
            disabled={!!editingEventType}
            style={editingEventType ? { background: '#F1F5F9', cursor: 'not-allowed' } : {}}
          />
        </div>
        <div className={styles.formGroup}>
          <label>Description</label>
          <textarea
            placeholder="Brief description of this event type"
            rows={2}
            className={styles.textarea}
            value={eventTypeFormData.description}
            onChange={(e) => setEventTypeFormData({ ...eventTypeFormData, description: e.target.value })}
          />
        </div>
        <div className={styles.formGroup}>
          <label>Icon</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {EVENT_ICON_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setEventTypeFormData({ ...eventTypeFormData, icon_name: opt.value })}
                style={{
                  width: '38px', height: '38px', borderRadius: '8px',
                  border: eventTypeFormData.icon_name === opt.value ? '2px solid #0C6B62' : '1px solid #E2E8F0',
                  background: eventTypeFormData.icon_name === opt.value ? '#E6F5F3' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                }}
                title={opt.label}
              >
                <opt.icon size={16} color={eventTypeFormData.icon_name === opt.value ? '#0C6B62' : '#64748B'} />
              </button>
            ))}
          </div>
        </div>
        <div className={styles.formGroup}>
          <label>Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="color"
              value={eventTypeFormData.color}
              onChange={(e) => setEventTypeFormData({ ...eventTypeFormData, color: e.target.value })}
              style={{ width: '48px', height: '36px', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', padding: '2px' }}
            />
            <input
              type="text"
              value={eventTypeFormData.color}
              onChange={(e) => setEventTypeFormData({ ...eventTypeFormData, color: e.target.value })}
              style={{ width: '100px', padding: '6px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontFamily: 'monospace' }}
            />
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: eventTypeFormData.color }} />
          </div>
        </div>
      </FormModal>
    </div>
  );

  const handleQuickSave = async (eventId: string) => {
    try {
      setActionLoading(true);
      await eventsApi.update(eventId, { title: quickEditData.title, event_date: quickEditData.event_date });
      setQuickEditEventId(null);
      setActionSuccess('Event updated!');
      setTimeout(() => setActionSuccess(''), 2000);
      refreshEvents();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const renderCustomEventsSection = () => {
    const query = eventSearchQuery.toLowerCase().trim();
    const filtered = query
      ? customEvents.filter(e => e.title.toLowerCase().includes(query) || (e.description || '').toLowerCase().includes(query))
      : customEvents;

    // Group events by event_type
    const grouped: Record<string, Event[]> = {};
    filtered.forEach(e => {
      const t = e.event_type || 'custom';
      if (!grouped[t]) grouped[t] = [];
      grouped[t].push(e);
    });

    // Sort groups by event type display_order
    const sortedTypes = Object.keys(grouped).sort((a, b) => {
      const aOrder = eventTypes.find(et => et.key === a)?.display_order ?? 99;
      const bOrder = eventTypes.find(et => et.key === b)?.display_order ?? 99;
      return aOrder - bOrder;
    });

    // ── INSIDE A FOLDER VIEW ──
    if (expandedEventType) {
      const typeInfo = eventTypes.find(et => et.key === expandedEventType);
      const typeColor = typeInfo?.color || '#6366F1';
      const typeName = typeInfo?.name || expandedEventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const iconName = typeInfo?.icon_name || 'party';
      const IconComp = getEventIconComponent(iconName);
      const eventsInFolder = (grouped[expandedEventType] || []).sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());

      return (
        <div>
          {/* Breadcrumb / back bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <button
              onClick={() => setExpandedEventType(null)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#64748B', transition: 'all 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F1F5F9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
            >
              <ChevronLeft size={16} /> Back
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${typeColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconComp size={18} style={{ color: typeColor }} />
              </div>
              <span style={{ fontSize: '17px', fontWeight: 700, color: '#1E293B' }}>{typeName}</span>
              <span style={{ fontSize: '12px', fontWeight: 600, padding: '2px 10px', borderRadius: '10px', background: `${typeColor}15`, color: typeColor }}>
                {eventsInFolder.length} event{eventsInFolder.length !== 1 ? 's' : ''}
              </span>
            </div>
            <Button onClick={() => { resetEventForm(); setShowCreateEventModal(true); }}>
              <Plus size={16} /> Add Event
            </Button>
          </div>

          {/* Search within folder */}
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input
              type="text"
              placeholder={`Search in ${typeName}...`}
              value={eventSearchQuery}
              onChange={(e) => setEventSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '10px 12px 10px 38px', borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '14px', outline: 'none', background: '#F8FAFC', boxSizing: 'border-box' }}
            />
            {eventSearchQuery && (
              <button onClick={() => setEventSearchQuery('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}>
                <X size={14} />
              </button>
            )}
          </div>

          {/* Event tiles grid */}
          {eventsInFolder.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94A3B8' }}>
              <Search size={32} style={{ marginBottom: '8px' }} />
              <p>No events match your search</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
              {eventsInFolder.map(event => (
                <div
                  key={event.id}
                  onDoubleClick={() => handleEditEvent(event)}
                  style={{
                    background: '#fff', borderRadius: '12px', padding: '14px', border: '1px solid #E2E8F0',
                    cursor: 'default', transition: 'all 0.15s', position: 'relative',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = typeColor; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
                >
                  {event.image_url ? (
                    <img src={event.image_url} alt="" style={{ width: '100%', height: '110px', borderRadius: '8px', objectFit: 'cover', marginBottom: '10px' }} />
                  ) : (
                    <div style={{ width: '100%', height: '70px', borderRadius: '8px', background: `${typeColor}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                      <IconComp size={28} style={{ color: typeColor, opacity: 0.4 }} />
                    </div>
                  )}
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '4px' }}>
                    {event.title}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: event.start_time ? '4px' : '8px' }}>
                    <Calendar size={11} />
                    <span>{new Date(event.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  {event.start_time && (
                    <div style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                      <Clock size={10} />
                      <span>
                        {new Date(`2000-01-01T${event.start_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        {event.end_time && ` – ${new Date(`2000-01-01T${event.end_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                      </span>
                    </div>
                  )}
                  {event.is_recurring && (
                    <div style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: '#F1F5F9', color: '#64748B', display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                      <Repeat size={10} />{event.recurring_frequency}
                    </div>
                  )}
                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '6px', borderTop: '1px solid #F1F5F9', paddingTop: '8px', marginTop: '4px' }}>
                    <button
                      onClick={() => handleEditEvent(event)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '6px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#64748B', transition: 'all 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F1F5F9'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => { setSelectedEvent(event); setShowDeleteEventModal(true); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#fff', cursor: 'pointer', color: '#94A3B8', transition: 'all 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#FEE2E2'; e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.borderColor = '#FECACA'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // ── SUPER TILES GRID (folder view) ──
    return (
    <div>
      {/* Search bar + Add Event */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
          <input
            type="text"
            placeholder="Search events..."
            value={eventSearchQuery}
            onChange={(e) => setEventSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '10px 12px 10px 38px', borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '14px', outline: 'none', background: '#F8FAFC' }}
          />
          {eventSearchQuery && (
            <button onClick={() => setEventSearchQuery('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}>
              <X size={14} />
            </button>
          )}
        </div>
        <Button onClick={() => { resetEventForm(); setShowCreateEventModal(true); }}>
          <Plus size={16} /> Add Event
        </Button>
      </div>

      {eventsLoading ? (
        <div className={styles.loadingState}>Loading events...</div>
      ) : customEvents.length === 0 ? (
        <div className={styles.emptyState}>
          <Calendar size={48} />
          <h3>No events yet</h3>
          <p>Create your first event to get started</p>
        </div>
      ) : sortedTypes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94A3B8' }}>
          <Search size={32} style={{ marginBottom: '8px' }} />
          <p>No events match "{eventSearchQuery}"</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
          {sortedTypes.map(typeKey => {
            const typeInfo = eventTypes.find(et => et.key === typeKey);
            const typeColor = typeInfo?.color || '#6366F1';
            const typeName = typeInfo?.name || typeKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const iconName = typeInfo?.icon_name || 'party';
            const IconComp = getEventIconComponent(iconName);
            const eventsInGroup = grouped[typeKey];
            const nextEvent = eventsInGroup.find(e => new Date(e.event_date) >= new Date());

            return (
              <div
                key={typeKey}
                onDoubleClick={() => { setEventSearchQuery(''); setExpandedEventType(typeKey); }}
                style={{
                  background: '#fff', borderRadius: '14px', padding: '20px', border: '1px solid #E2E8F0',
                  cursor: 'default', transition: 'all 0.15s', userSelect: 'none', borderLeft: `4px solid ${typeColor}`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = typeColor; e.currentTarget.style.borderLeftColor = typeColor; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.borderLeftColor = typeColor; }}
              >
                <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: `${typeColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>
                  <IconComp size={24} style={{ color: typeColor }} />
                </div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#1E293B', marginBottom: '6px' }}>{typeName}</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: typeColor, marginBottom: '8px' }}>
                  {eventsInGroup.length} event{eventsInGroup.length !== 1 ? 's' : ''}
                </div>
                {nextEvent && (
                  <div style={{ fontSize: '12px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={11} />
                    Next: {new Date(nextEvent.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '10px' }}>Double-click to open</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
  };

  const renderEventTemplatesSection = () => {
    const isSystemType = (type: string) => ['birthday', 'work_anniversary'].includes(type);
    const getPlaceholderHint = (type: string) => isSystemType(type)
      ? "Placeholders: {{name}}, {{years}}, {{years_text}}"
      : "Placeholders: {{title}}, {{description}}, {{event_date}}";

    return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <p style={{ color: '#64748B', margin: 0, flex: 1 }}>
          Customize how events appear on the Events page. System types use placeholders: <code>{'{{name}}'}</code>, <code>{'{{years}}'}</code>, <code>{'{{years_text}}'}</code>. Custom types use: <code>{'{{title}}'}</code>, <code>{'{{description}}'}</code>, <code>{'{{event_date}}'}</code>
        </p>
        {getAvailableTypesForEventTemplate().length > 0 && (
          <Button onClick={() => { setCreateEventTemplateFormData({ template_type: '', title_template: '', message_template: '', icon_name: 'party', image_url: '' }); setShowCreateEventTemplateModal(true); }}>
            <Plus size={16} /> Add Template
          </Button>
        )}
      </div>
      {eventTemplates.map(template => {
        const isEditing = editingEventTemplateId === template.id;
        const matchedType = eventTypes.find(et => et.key === template.template_type);
        const typeColor = matchedType?.color || (template.template_type === 'birthday' ? '#E8438C' : '#D97706');
        const typeName = matchedType?.name || (template.template_type === 'birthday' ? 'Birthday' : template.template_type === 'work_anniversary' ? 'Work Anniversary' : template.template_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
        const iconName = isEditing ? eventTemplateFormData.icon_name : (template.icon_name || matchedType?.icon_name || 'cake');
        const IconComp = getEventIconComponent(iconName);
        return (
          <div key={template.id} className={styles.announcementCard} style={{ marginBottom: '16px' }}>
            <div className={styles.announcementCardHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${typeColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconComp size={20} color={typeColor} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>{typeName} Template</h3>
                  <span style={{ color: '#64748B', fontSize: '13px' }}>Controls event display on the Events page</span>
                </div>
              </div>
              {!isEditing ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button variant="outline" size="sm" onClick={() => handleEventTemplateEdit(template)}>
                    <Edit2 size={14} /> Edit
                  </Button>
                  {!isSystemType(template.template_type) && (
                    <Button variant="outline" size="sm" onClick={() => handleDeleteEventTemplate(template.id)} style={{ color: '#EF4444' }}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button variant="ghost" size="sm" onClick={() => setEditingEventTemplateId(null)}>Cancel</Button>
                  <Button size="sm" onClick={() => handleEventTemplateSave(template.id)} disabled={actionLoading}>
                    <Save size={14} /> Save
                  </Button>
                </div>
              )}
            </div>
            {isEditing ? (
              <div style={{ marginTop: '16px' }}>
                <div className={styles.formGroup}>
                  <label>Title Template</label>
                  <input
                    type="text"
                    value={eventTemplateFormData.title_template}
                    onChange={(e) => setEventTemplateFormData({ ...eventTemplateFormData, title_template: e.target.value })}
                  />
                  <span className={styles.fieldHint}>{getPlaceholderHint(template.template_type)}</span>
                </div>
                <div className={styles.formGroup}>
                  <label>Message Template</label>
                  <textarea
                    rows={4}
                    className={styles.textarea}
                    value={eventTemplateFormData.message_template}
                    onChange={(e) => setEventTemplateFormData({ ...eventTemplateFormData, message_template: e.target.value })}
                  />
                  <span className={styles.fieldHint}>{getPlaceholderHint(template.template_type)}</span>
                </div>
                <div className={styles.formGroup}>
                  <label>Icon</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {EVENT_ICON_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setEventTemplateFormData({ ...eventTemplateFormData, icon_name: opt.value })}
                        style={{
                          width: '40px', height: '40px', borderRadius: '8px', border: eventTemplateFormData.icon_name === opt.value ? '2px solid #0C6B62' : '1px solid #E2E8F0',
                          background: eventTemplateFormData.icon_name === opt.value ? '#E6F5F3' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                        }}
                      >
                        <opt.icon size={18} color={eventTemplateFormData.icon_name === opt.value ? '#0C6B62' : '#64748B'} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Template Image</label>
                  <div className={styles.imageUploadArea} onClick={() => eventTemplateImageInputRef.current?.click()}>
                    {eventTemplateFormData.image_url ? (
                      <div className={styles.imagePreview}>
                        <img src={eventTemplateFormData.image_url} alt="Preview" />
                        <button type="button" className={styles.removeImageBtn} onClick={(e) => { e.stopPropagation(); setEventTemplateFormData({ ...eventTemplateFormData, image_url: '' }); }}>
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className={styles.uploadPlaceholder}>
                        <Upload size={32} />
                        <span>Click to upload image</span>
                      </div>
                    )}
                  </div>
                  <input ref={eventTemplateImageInputRef} type="file" accept="image/*" onChange={handleEventTemplateImageUpload} style={{ display: 'none' }} />
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '12px', padding: '12px', background: '#F8FAFC', borderRadius: '8px' }}>
                <p style={{ margin: '0 0 4px', fontWeight: 500 }}>{template.title_template}</p>
                <p style={{ margin: 0, color: '#64748B', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{template.message_template}</p>
              </div>
            )}
          </div>
        );
      })}
      {showCreateEventTemplateModal && (
        <FormModal
          isOpen={showCreateEventTemplateModal}
          onClose={() => setShowCreateEventTemplateModal(false)}
          title="Add Event Display Template"
          subtitle="Configure how events of a given type are displayed"
          icon={<Calendar size={16} />}
          onSubmit={handleCreateEventTemplate}
          submitLabel="Create Template"
          submitLoadingLabel="Creating…"
          submitLoading={actionLoading}
          submitDisabled={!createEventTemplateFormData.template_type}
        >
          <div className={styles.formGroup}>
            <label>Event Type *</label>
            <select
              value={createEventTemplateFormData.template_type}
              onChange={(e) => setCreateEventTemplateFormData({ ...createEventTemplateFormData, template_type: e.target.value })}
              className={styles.select}
            >
              <option value="">Select an event type...</option>
              {getAvailableTypesForEventTemplate().map(et => (
                <option key={et.key} value={et.key}>{et.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Title Template</label>
            <input
              type="text"
              placeholder="e.g. ☕ {{title}}"
              value={createEventTemplateFormData.title_template}
              onChange={(e) => setCreateEventTemplateFormData({ ...createEventTemplateFormData, title_template: e.target.value })}
            />
            <span className={styles.fieldHint}>Placeholders: {'{{title}}'}, {'{{description}}'}, {'{{event_date}}'}</span>
          </div>
          <div className={styles.formGroup}>
            <label>Message Template</label>
            <textarea
              rows={4}
              className={styles.textarea}
              placeholder="e.g. Join us for {{title}}! {{description}}"
              value={createEventTemplateFormData.message_template}
              onChange={(e) => setCreateEventTemplateFormData({ ...createEventTemplateFormData, message_template: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Icon</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {EVENT_ICON_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCreateEventTemplateFormData({ ...createEventTemplateFormData, icon_name: opt.value })}
                  style={{
                    width: '38px', height: '38px', borderRadius: '8px',
                    border: createEventTemplateFormData.icon_name === opt.value ? '2px solid #0C6B62' : '1px solid #E2E8F0',
                    background: createEventTemplateFormData.icon_name === opt.value ? '#E6F5F3' : 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                  }}
                  title={opt.label}
                >
                  <opt.icon size={16} color={createEventTemplateFormData.icon_name === opt.value ? '#0C6B62' : '#64748B'} />
                </button>
              ))}
            </div>
          </div>
        </FormModal>
      )}
    </div>
  );
  };

  const renderAnnouncementTemplatesSection = () => {
    const isSystemType = (type: string) => ['birthday', 'work_anniversary'].includes(type);
    const getPlaceholderHint = (type: string) => isSystemType(type)
      ? "Placeholders: {{name}}, {{years}}, {{years_text}}"
      : "Placeholders: {{title}}, {{description}}, {{event_date}}";

    return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <p style={{ color: '#64748B', margin: 0, flex: 1 }}>
          Configure which event types automatically post to Announcements. Each type has its own template and timing.
        </p>
        {getAvailableTypesForAnnouncementTemplate().length > 0 && (
          <Button onClick={() => { setCreateAnnouncementTemplateFormData({ template_type: '', title_template: '', message_template: '', image_url: '', auto_post_enabled: false, post_timing_days_before: 0 }); setShowCreateAnnouncementTemplateModal(true); }}>
            <Plus size={16} /> Add Template
          </Button>
        )}
      </div>
      {announcementTemplates.map(template => {
        const isEditing = editingAnnouncementTemplateId === template.id;
        const matchedType = eventTypes.find(et => et.key === template.template_type);
        const typeColor = matchedType?.color || (template.template_type === 'birthday' ? '#E8438C' : '#D97706');
        const typeName = matchedType?.name || (template.template_type === 'birthday' ? 'Birthday' : template.template_type === 'work_anniversary' ? 'Work Anniversary' : template.template_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
        const iconName = matchedType?.icon_name || (template.template_type === 'birthday' ? 'cake' : 'briefcase');
        const IconComp = getEventIconComponent(iconName);
        return (
          <div key={template.id} className={styles.announcementCard} style={{ marginBottom: '16px' }}>
            <div className={styles.announcementCardHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${typeColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconComp size={20} color={typeColor} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>{typeName} Announcements</h3>
                  <span style={{ color: '#64748B', fontSize: '13px' }}>Auto-post to Announcements page</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={() => handleAnnouncementToggle(template)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  {template.auto_post_enabled ? (
                    <ToggleRight size={32} color="#0C6B62" />
                  ) : (
                    <ToggleLeft size={32} color="#94A3B8" />
                  )}
                </button>
                {!isEditing ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button variant="outline" size="sm" onClick={() => handleAnnouncementTemplateEdit(template)}>
                      <Edit2 size={14} /> Edit
                    </Button>
                    {!isSystemType(template.template_type) && (
                      <Button variant="outline" size="sm" onClick={() => handleDeleteAnnouncementTemplate(template.id)} style={{ color: '#EF4444' }}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button variant="ghost" size="sm" onClick={() => setEditingAnnouncementTemplateId(null)}>Cancel</Button>
                    <Button size="sm" onClick={() => handleAnnouncementTemplateSave(template.id)} disabled={actionLoading}>
                      <Save size={14} /> Save
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Status row */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '12px', padding: '10px 14px', background: '#F8FAFC', borderRadius: '8px' }}>
              <Badge variant={template.auto_post_enabled ? 'success' : 'default'} size="sm">
                {template.auto_post_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <span style={{ color: '#64748B', fontSize: '13px' }}>
                Posts {template.post_timing_days_before === 0 ? 'on the day' : `${template.post_timing_days_before} day${template.post_timing_days_before > 1 ? 's' : ''} before`} at 9:00 AM IST
              </span>
            </div>

            {isEditing ? (
              <div style={{ marginTop: '16px' }}>
                <div className={styles.formGroup}>
                  <label>Post Timing</label>
                  <select
                    value={announcementTemplateFormData.post_timing_days_before}
                    onChange={(e) => setAnnouncementTemplateFormData({ ...announcementTemplateFormData, post_timing_days_before: parseInt(e.target.value) })}
                    className={styles.select}
                  >
                    <option value={0}>On the day of the event</option>
                    <option value={1}>1 day before</option>
                    <option value={2}>2 days before</option>
                    <option value={3}>3 days before</option>
                    <option value={4}>4 days before</option>
                    <option value={5}>5 days before</option>
                    <option value={6}>6 days before</option>
                    <option value={7}>7 days before</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Title Template</label>
                  <input
                    type="text"
                    value={announcementTemplateFormData.title_template}
                    onChange={(e) => setAnnouncementTemplateFormData({ ...announcementTemplateFormData, title_template: e.target.value })}
                  />
                  <span className={styles.fieldHint}>{getPlaceholderHint(template.template_type)}</span>
                </div>
                <div className={styles.formGroup}>
                  <label>Message Template</label>
                  <textarea
                    rows={5}
                    className={styles.textarea}
                    value={announcementTemplateFormData.message_template}
                    onChange={(e) => setAnnouncementTemplateFormData({ ...announcementTemplateFormData, message_template: e.target.value })}
                  />
                  <span style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px', display: 'block' }}>
                    Placeholders: {'{{title}}'}, {'{{description}}'}, {'{{event_date}}'}, {'{{event_time}}'}, {'{{link}}'}
                  </span>
                </div>
                <div className={styles.formGroup}>
                  <label>Announcement Image</label>
                  <div className={styles.imageUploadArea} onClick={() => announcementTemplateImageInputRef.current?.click()}>
                    {announcementTemplateFormData.image_url ? (
                      <div className={styles.imagePreview}>
                        <img src={announcementTemplateFormData.image_url} alt="Preview" />
                        <button type="button" className={styles.removeImageBtn} onClick={(e) => { e.stopPropagation(); setAnnouncementTemplateFormData({ ...announcementTemplateFormData, image_url: '' }); }}>
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className={styles.uploadPlaceholder}>
                        <Upload size={32} />
                        <span>Click to upload image</span>
                      </div>
                    )}
                  </div>
                  <input ref={announcementTemplateImageInputRef} type="file" accept="image/*" onChange={handleAnnouncementTemplateImageUpload} style={{ display: 'none' }} />
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '12px', padding: '12px', background: '#F8FAFC', borderRadius: '8px' }}>
                <p style={{ margin: '0 0 4px', fontWeight: 500 }}>{template.title_template}</p>
                <p style={{ margin: 0, color: '#64748B', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{template.message_template}</p>
              </div>
            )}
          </div>
        );
      })}
      {showCreateAnnouncementTemplateModal && (
        <FormModal
          isOpen={showCreateAnnouncementTemplateModal}
          onClose={() => setShowCreateAnnouncementTemplateModal(false)}
          title="Add Announcement Auto-Post Template"
          subtitle="Automate announcements for a given event type"
          icon={<Bell size={16} />}
          onSubmit={handleCreateAnnouncementTemplate}
          submitLabel="Create Template"
          submitLoadingLabel="Creating…"
          submitLoading={actionLoading}
          submitDisabled={!createAnnouncementTemplateFormData.template_type}
        >
          <div className={styles.formGroup}>
            <label>Event Type *</label>
            <select
              value={createAnnouncementTemplateFormData.template_type}
              onChange={(e) => setCreateAnnouncementTemplateFormData({ ...createAnnouncementTemplateFormData, template_type: e.target.value })}
              className={styles.select}
            >
              <option value="">Select an event type...</option>
              {getAvailableTypesForAnnouncementTemplate().map(et => (
                <option key={et.key} value={et.key}>{et.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Title Template</label>
            <input
              type="text"
              placeholder="e.g. {{title}}"
              value={createAnnouncementTemplateFormData.title_template}
              onChange={(e) => setCreateAnnouncementTemplateFormData({ ...createAnnouncementTemplateFormData, title_template: e.target.value })}
            />
            <span className={styles.fieldHint}>Placeholders: {'{{title}}'}, {'{{description}}'}, {'{{event_date}}'}</span>
          </div>
          <div className={styles.formGroup}>
            <label>Message Template</label>
            <textarea
              rows={4}
              className={styles.textarea}
              placeholder="e.g. {{description}}"
              value={createAnnouncementTemplateFormData.message_template}
              onChange={(e) => setCreateAnnouncementTemplateFormData({ ...createAnnouncementTemplateFormData, message_template: e.target.value })}
            />
            <span style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px', display: 'block' }}>
              Placeholders: {'{{title}}'}, {'{{description}}'}, {'{{event_date}}'}, {'{{event_time}}'}
            </span>
          </div>
          <div className={styles.formGroup}>
            <label>Auto-Post</label>
            <select
              value={createAnnouncementTemplateFormData.auto_post_enabled ? 'true' : 'false'}
              onChange={(e) => setCreateAnnouncementTemplateFormData({ ...createAnnouncementTemplateFormData, auto_post_enabled: e.target.value === 'true' })}
              className={styles.select}
            >
              <option value="false">Disabled</option>
              <option value="true">Enabled</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Post Timing</label>
            <select
              value={createAnnouncementTemplateFormData.post_timing_days_before}
              onChange={(e) => setCreateAnnouncementTemplateFormData({ ...createAnnouncementTemplateFormData, post_timing_days_before: parseInt(e.target.value) })}
              className={styles.select}
            >
              <option value={0}>On the day of the event</option>
              <option value={1}>1 day before</option>
              <option value={2}>2 days before</option>
              <option value={3}>3 days before</option>
              <option value={5}>5 days before</option>
              <option value={7}>7 days before</option>
            </select>
          </div>
        </FormModal>
      )}
    </div>
  );
  };

  const renderEventsManagement = () => (
    <div className={styles.tabContent}>
      <div className={styles.contentHeader}>
        <div>
          <h2>Events Management</h2>
          <p>Manage custom events, display templates, and announcement auto-posting</p>
        </div>
      </div>

      {/* Sub-tabs — Event Types / Templates / Announcement Auto-Post are
          superadmin-only on the backend, so we hide them from admin role. */}
      <div className={styles.subTabs}>
        {currentUser?.role === 'superadmin' && (
          <button
            className={`${styles.subTab} ${eventsSubTab === 'event-types' ? styles.subTabActive : ''}`}
            onClick={() => setEventsSubTab('event-types')}
          >
            Event Types
          </button>
        )}
        <button
          className={`${styles.subTab} ${eventsSubTab === 'custom' ? styles.subTabActive : ''}`}
          onClick={() => setEventsSubTab('custom')}
        >
          Create an Event
        </button>
        {currentUser?.role === 'superadmin' && (
          <>
            <button
              className={`${styles.subTab} ${eventsSubTab === 'event-templates' ? styles.subTabActive : ''}`}
              onClick={() => setEventsSubTab('event-templates')}
            >
              Event Display Templates
            </button>
            <button
              className={`${styles.subTab} ${eventsSubTab === 'announcement-templates' ? styles.subTabActive : ''}`}
              onClick={() => setEventsSubTab('announcement-templates')}
            >
              Announcement Auto-Post
            </button>
          </>
        )}
      </div>

      {eventsSubTab === 'event-types' && currentUser?.role === 'superadmin' && renderEventTypesSection()}
      {eventsSubTab === 'custom' && renderCustomEventsSection()}
      {eventsSubTab === 'event-templates' && currentUser?.role === 'superadmin' && renderEventTemplatesSection()}
      {eventsSubTab === 'announcement-templates' && currentUser?.role === 'superadmin' && renderAnnouncementTemplatesSection()}

      {/* Create Event Modal */}
      <Modal
        isOpen={showCreateEventModal}
        onClose={() => { setShowCreateEventModal(false); resetEventForm(); }}
        size="lg"
      >
        <div className={styles.evtModalHero}>
          <div className={styles.evtModalHeroLeft}>
            <div className={styles.evtHeroIconBox}>
              <Calendar size={16} color="#9FE1CB" />
            </div>
            <div>
              <div className={styles.evtModalTitle}>Create Event</div>
              <div className={styles.evtModalSub}>Add a new event to the calendar</div>
            </div>
          </div>
          <button className={styles.evtModalCloseBtn} onClick={() => { setShowCreateEventModal(false); resetEventForm(); }}>
            <X size={14} />
          </button>
        </div>
        {actionError && <div className={styles.evtErrorMessage}><AlertCircle size={16} /> {actionError}</div>}
        {renderEventForm(false)}
        <div className={styles.evtModalFooter}>
          <div className={styles.evtFooterHint}>
            <Calendar size={12} /> Visible to all members
          </div>
          <div className={styles.evtFooterActions}>
            <button className={styles.evtBtnCancel} onClick={() => { setShowCreateEventModal(false); resetEventForm(); }}>
              Cancel
            </button>
            <button className={styles.evtBtnPublish} onClick={handleCreateEvent} disabled={actionLoading}>
              {actionLoading ? 'Creating…' : 'Create Event'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Event Modal */}
      <Modal
        isOpen={showEditEventModal}
        onClose={() => { setShowEditEventModal(false); resetEventForm(); }}
        size="lg"
      >
        <div className={styles.evtModalHero}>
          <div className={styles.evtModalHeroLeft}>
            <div className={styles.evtHeroIconBox}>
              <Edit2 size={16} color="#9FE1CB" />
            </div>
            <div>
              <div className={styles.evtModalTitle}>Edit Event</div>
              <div className={styles.evtModalSub}>Update event details</div>
            </div>
          </div>
          <button className={styles.evtModalCloseBtn} onClick={() => { setShowEditEventModal(false); resetEventForm(); }}>
            <X size={14} />
          </button>
        </div>
        {actionError && <div className={styles.evtErrorMessage}><AlertCircle size={16} /> {actionError}</div>}
        {renderEventForm(true)}
        <div className={styles.evtModalFooter}>
          <div className={styles.evtFooterHint}>
            <Calendar size={12} /> Visible to all members
          </div>
          <div className={styles.evtFooterActions}>
            <button className={styles.evtBtnCancel} onClick={() => { setShowEditEventModal(false); resetEventForm(); }}>
              Cancel
            </button>
            <button className={styles.evtBtnPublish} onClick={handleUpdateEvent} disabled={actionLoading}>
              {actionLoading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Event Modal */}
      <Modal isOpen={showDeleteEventModal} onClose={() => { setShowDeleteEventModal(false); setSelectedEvent(null); }} title="Delete Event">
        <p>Are you sure you want to delete <strong>{selectedEvent?.title}</strong>? This action cannot be undone.</p>
        <div className={styles.formActions}>
          <Button variant="ghost" onClick={() => { setShowDeleteEventModal(false); setSelectedEvent(null); }}>Cancel</Button>
          <Button onClick={handleDeleteEvent} disabled={actionLoading}>
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  );

  const renderIntegrations = () => {
    const baseUrl = window.location.origin.includes('localhost')
      ? 'https://ripple.matasma.in'
      : window.location.origin;

    const xoxodayEndpoints = [
      { name: 'Balance API', url: `${baseUrl}/api/xoxoday/balance`, method: 'POST', description: 'Xoxoday calls this to check user point balance' },
      { name: 'Profile API', url: `${baseUrl}/api/xoxoday/profile`, method: 'POST', description: 'Xoxoday calls this for fraud prevention' },
      { name: 'Redemption API', url: `${baseUrl}/api/xoxoday/redemption`, method: 'POST', description: 'Xoxoday calls this after order placement' },
      { name: 'Refund API', url: `${baseUrl}/api/xoxoday/refund`, method: 'POST', description: 'Xoxoday calls this for refunds' }
    ];

    const newsProvidersList = [
      { id: 'newsapi', name: 'NewsAPI.org', description: 'Free tier: 100 requests/day', requiresKey: true },
      { id: 'gnews', name: 'GNews', description: 'Free tier: 100 requests/day', requiresKey: true },
      { id: 'mediastack', name: 'Mediastack', description: 'Free tier: 500 requests/month', requiresKey: true }
    ];

    const quotesProvidersList = [
      { id: 'quotable', name: 'Quotable', description: 'Free, no API key required', requiresKey: false },
      { id: 'api-ninjas', name: 'API Ninjas', description: 'Free tier: 10,000 requests/month', requiresKey: true },
      { id: 'zenquotes', name: 'ZenQuotes', description: 'Free, no API key required', requiresKey: false }
    ];

    return (
      <div className={styles.tabContent}>
        <div className={styles.contentHeader}>
          <div>
            <h2>Integrations</h2>
            <p>Manage third-party integrations and API configurations</p>
          </div>
        </div>

        <div className={styles.integrationsGrid}>
          {/* News Feed Integration Card */}
          <Card className={styles.integrationCard}>
            <CardContent>
              <div
                className={styles.integrationHeader}
                onClick={() => setExpandedIntegration(expandedIntegration === 'news' ? null : 'news')}
              >
                <div className={styles.integrationTitle}>
                  <Newspaper size={24} className={styles.integrationIcon} />
                  <div>
                    <h3>News Feed</h3>
                    <p>Display industry news on the dashboard</p>
                  </div>
                </div>
                <div className={styles.integrationHeaderRight}>
                  <Badge variant={newsSources.length > 0 ? 'success' : 'default'}>
                    {newsSources.length > 0 ? `${newsSources.length} Source(s)` : 'Not Configured'}
                  </Badge>
                  {expandedIntegration === 'news' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {expandedIntegration === 'news' && (
                <div className={styles.integrationBody}>
                  <div className={styles.sourcesList}>
                    <div className={styles.sourcesHeader}>
                      <h4>Configured Sources</h4>
                      <Button size="sm" onClick={() => { resetNewsForm(); setShowAddNewsModal(true); }}>
                        <Plus size={14} /> Add Source
                      </Button>
                    </div>

                    {newsSources.length === 0 ? (
                      <div className={styles.emptySourceState}>
                        <p>No news sources configured. Add a source to display news on the dashboard.</p>
                      </div>
                    ) : (
                      <div className={styles.sourcesGrid}>
                        {newsSources.map((source) => (
                          <div key={source.id} className={`${styles.sourceCard} ${!source.is_enabled ? styles.disabled : ''}`}>
                            <div className={styles.sourceInfo}>
                              <h5>{getProviderName(source.provider, 'news')}</h5>
                              <span className={styles.sourceCategory}>{source.category}</span>
                              <Badge variant={source.is_enabled ? 'success' : 'default'} size="sm">
                                {source.is_enabled ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            <div className={styles.sourceActions}>
                              <Button variant="ghost" size="sm" onClick={() => handleEditNewsSource(source)}>
                                <Edit2 size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteNewsSource(source.provider)}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quotes Integration Card */}
          <Card className={styles.integrationCard}>
            <CardContent>
              <div
                className={styles.integrationHeader}
                onClick={() => setExpandedIntegration(expandedIntegration === 'quotes' ? null : 'quotes')}
              >
                <div className={styles.integrationTitle}>
                  <Quote size={24} className={styles.integrationIcon} />
                  <div>
                    <h3>Daily Quotes</h3>
                    <p>Show motivational quotes on the dashboard</p>
                  </div>
                </div>
                <div className={styles.integrationHeaderRight}>
                  <Badge variant={quotesSources.length > 0 ? 'success' : 'default'}>
                    {quotesSources.length > 0 ? `${quotesSources.length} Source(s)` : 'Not Configured'}
                  </Badge>
                  {expandedIntegration === 'quotes' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {expandedIntegration === 'quotes' && (
                <div className={styles.integrationBody}>
                  <div className={styles.sourcesList}>
                    <div className={styles.sourcesHeader}>
                      <h4>Configured Sources</h4>
                      <Button size="sm" onClick={() => { resetQuotesForm(); setShowAddQuotesModal(true); }}>
                        <Plus size={14} /> Add Source
                      </Button>
                    </div>

                    {quotesSources.length === 0 ? (
                      <div className={styles.emptySourceState}>
                        <p>No quotes sources configured. Add a source to display quotes on the dashboard.</p>
                      </div>
                    ) : (
                      <div className={styles.sourcesGrid}>
                        {quotesSources.map((source) => (
                          <div key={source.id} className={`${styles.sourceCard} ${!source.is_enabled ? styles.disabled : ''}`}>
                            <div className={styles.sourceInfo}>
                              <h5>{getProviderName(source.provider, 'quotes')}</h5>
                              <span className={styles.sourceCategory}>{source.category}</span>
                              <Badge variant={source.is_enabled ? 'success' : 'default'} size="sm">
                                {source.is_enabled ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            <div className={styles.sourceActions}>
                              <Button variant="ghost" size="sm" onClick={() => handleEditQuotesSource(source)}>
                                <Edit2 size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteQuotesSource(source.provider)}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Xoxoday Integration Card */}
          <Card className={styles.integrationCard}>
            <CardContent>
              <div
                className={styles.integrationHeader}
                onClick={() => setExpandedIntegration(expandedIntegration === 'xoxoday' ? null : 'xoxoday')}
              >
                <div className={styles.integrationTitle}>
                  <Gift size={24} className={styles.integrationIcon} />
                  <div>
                    <h3>Xoxoday Rewards</h3>
                    <p>Enable users to redeem points for rewards</p>
                  </div>
                </div>
                <div className={styles.integrationHeaderRight}>
                  {xoxodayHealth?.status === 'ok' ? (
                    <Badge variant="success">
                      <CheckCircle size={14} /> Connected
                    </Badge>
                  ) : (
                    <Badge variant="warning">
                      <AlertCircle size={14} /> Not Configured
                    </Badge>
                  )}
                  {expandedIntegration === 'xoxoday' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {expandedIntegration === 'xoxoday' && (
                <div className={styles.integrationBody}>
                  {/* Status Info */}
                  {xoxodayConfig && xoxodayHealth && (
                    <div className={styles.integrationStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Environment</span>
                        <span className={styles.statValue}>{xoxodayConfig.environment || 'staging'}</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Token Status</span>
                        <span className={`${styles.statValue} ${xoxodayHealth.tokenValid ? styles.valid : styles.invalid}`}>
                          {xoxodayHealth.tokenValid ? 'Valid' : 'Expired'}
                        </span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Token Expires In</span>
                        <span className={styles.statValue}>{xoxodayHealth.daysUntilExpiry} days</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Last Updated</span>
                        <span className={styles.statValue}>
                          {xoxodayConfig.lastUpdated ? new Date(xoxodayConfig.lastUpdated).toLocaleDateString() : 'Never'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className={styles.integrationActions}>
                    <Button variant="primary" onClick={() => setShowXoxodayTokenModal(true)}>
                      <Key size={16} /> Update Tokens
                    </Button>
                    <Button variant="outline" onClick={fetchXoxodayData}>
                      <RefreshCw size={16} /> Refresh Status
                    </Button>
                    <a
                      href="https://stores.xoxoday.com/marketplace/login"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.externalLink}
                    >
                      <Button variant="outline">
                        <ExternalLink size={16} /> Xoxoday Dashboard
                      </Button>
                    </a>
                  </div>

                  {/* API Endpoints */}
                  <div className={styles.endpointsSection}>
                    <div className={styles.endpointsHeader}>
                      <h4>API Endpoints</h4>
                      <p>Configure these in Xoxoday dashboard under Storefront Integration</p>
                    </div>

                    <div className={styles.endpointsList}>
                      {xoxodayEndpoints.map((endpoint) => (
                        <div key={endpoint.name} className={styles.endpointItem}>
                          <div className={styles.endpointInfo}>
                            <div className={styles.endpointName}>
                              <span className={styles.methodBadge}>{endpoint.method}</span>
                              {endpoint.name}
                            </div>
                            <code className={styles.endpointUrl}>{endpoint.url}</code>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(endpoint.url, endpoint.name)}
                          >
                            {copiedEndpoint === endpoint.name ? <Check size={14} /> : <Copy size={14} />}
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Authentication Info */}
                    <div className={styles.authInfo}>
                      <h4>Authentication Header</h4>
                      <div className={styles.authHeader}>
                        <code>X-API-Key: ripple-xoxoday-shared-secret-2026</code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard('ripple-xoxoday-shared-secret-2026', 'apiKey')}
                        >
                          {copiedEndpoint === 'apiKey' ? <Check size={14} /> : <Copy size={14} />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Documentation */}
                  <div className={styles.docsCard}>
                    <h4>Documentation</h4>
                    <div className={styles.docLinks}>
                      <a href="https://developers.xoxoday.com/reference/about-marketplace-integration" target="_blank" rel="noopener noreferrer" className={styles.docLink}>
                        <ExternalLink size={14} /> Storefront Integration Docs
                      </a>
                      <a href="https://xoxoday.gitbook.io/plum/developer-resources/storefront-integration" target="_blank" rel="noopener noreferrer" className={styles.docLink}>
                        <ExternalLink size={14} /> Complete Integration Guide
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const handleRunSecurityAudit = async () => {
    setAuditLoading(true);
    setActionError('');
    try {
      const response = await settingsApi.runSecurityAudit();
      setAuditResult(response.data);
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to run security audit');
    } finally {
      setAuditLoading(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertCircle size={16} />;
      case 'high': return <AlertTriangle size={16} />;
      case 'medium': return <AlertTriangle size={16} />;
      case 'low': return <Info size={16} />;
      case 'info': return <Info size={16} />;
      default: return <Info size={16} />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#d97706';
      case 'low': return '#2563eb';
      case 'info': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#10b981';
    if (score >= 70) return '#d97706';
    if (score >= 50) return '#ea580c';
    return '#dc2626';
  };

  const renderSecurity = () => (
    <div className={styles.tabContent}>
      <div className={styles.contentHeader}>
        <div>
          <h2>Security Settings</h2>
          <p>Manage security and access controls for your organization</p>
        </div>
      </div>

      <div className={styles.settingsGrid}>
        <Card>
          <CardContent>
            <div className={styles.settingsSection}>
              <div className={styles.sectionHeader}>
                <Globe size={20} />
                <h3>Access Control Overview</h3>
              </div>
              <div className={styles.settingsList}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Active Users</span>
                  <span className={styles.infoValue}>{users.filter(u => u.is_active).length} users currently active</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Admin Users</span>
                  <span className={styles.infoValue}>{users.filter(u => u.role === 'admin' || u.role === 'superadmin').length} administrators</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Last Security Audit</span>
                  <span className={styles.infoValue}>
                    {auditResult
                      ? new Date(auditResult.audit_date).toLocaleString()
                      : 'Never performed'}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                className={styles.auditBtn}
                onClick={handleRunSecurityAudit}
                disabled={auditLoading}
              >
                {auditLoading ? (
                  <><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Running Audit...</>
                ) : (
                  <><Shield size={16} /> Run Security Audit</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {actionError && (
        <div className={styles.errorMessage} style={{ marginTop: 16 }}>
          {actionError}
        </div>
      )}

      {auditResult && (
        <>
          {/* Score Card */}
          <div style={{ marginTop: 20 }}>
            <Card>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '8px 0' }}>
                  <div style={{
                    width: 80, height: 80, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 28, fontWeight: 700,
                    color: getScoreColor(auditResult.score),
                    border: `4px solid ${getScoreColor(auditResult.score)}`,
                    flexShrink: 0,
                  }}>
                    {auditResult.score}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18 }}>
                      Security Score: {auditResult.score >= 90 ? 'Excellent' : auditResult.score >= 70 ? 'Good' : auditResult.score >= 50 ? 'Needs Attention' : 'Critical'}
                    </h3>
                    <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 14 }}>
                      {auditResult.findings_count === 0
                        ? 'No security issues found.'
                        : `${auditResult.findings_count} finding${auditResult.findings_count !== 1 ? 's' : ''} detected across your organization.`}
                    </p>
                    <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                      <span>Users: {auditResult.stats.total_users}</span>
                      <span>Features: {auditResult.stats.total_features}</span>
                      <span>Permissions: {auditResult.stats.total_permissions}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Findings */}
          {auditResult.findings.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Findings</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {auditResult.findings.map((finding: any) => (
                  <Card key={finding.id}>
                    <CardContent>
                      <div
                        style={{ cursor: finding.details ? 'pointer' : 'default' }}
                        onClick={() => finding.details && setExpandedFinding(expandedFinding === finding.id ? null : finding.id)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ color: getSeverityColor(finding.severity), display: 'flex', flexShrink: 0 }}>
                            {getSeverityIcon(finding.severity)}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{
                                fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                                padding: '2px 8px', borderRadius: 4,
                                background: `${getSeverityColor(finding.severity)}18`,
                                color: getSeverityColor(finding.severity),
                              }}>
                                {finding.severity}
                              </span>
                              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{finding.category}</span>
                              {finding.affected_count && (
                                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                                  ({finding.affected_count} affected)
                                </span>
                              )}
                            </div>
                            <h4 style={{ margin: '4px 0 2px', fontSize: 14, fontWeight: 500 }}>{finding.title}</h4>
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                              {finding.description}
                            </p>
                          </div>
                          {finding.details && (
                            <span style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
                              {expandedFinding === finding.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </span>
                          )}
                        </div>
                        {expandedFinding === finding.id && finding.details && (
                          <div style={{
                            marginTop: 12, paddingTop: 12,
                            borderTop: '1px solid var(--border)',
                          }}>
                            <div style={{
                              background: 'var(--bg-secondary)', borderRadius: 8, padding: 12,
                              fontSize: 13, maxHeight: 200, overflowY: 'auto',
                            }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    {Object.keys(finding.details[0] || {}).map((key: string) => (
                                      <th key={key} style={{
                                        textAlign: 'left', padding: '4px 8px', fontWeight: 600,
                                        borderBottom: '1px solid var(--border)', fontSize: 12,
                                        textTransform: 'capitalize',
                                      }}>
                                        {key.replace(/_/g, ' ')}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {finding.details.map((detail: any, idx: number) => (
                                    <tr key={idx}>
                                      {Object.values(detail).map((val: any, i: number) => (
                                        <td key={i} style={{
                                          padding: '4px 8px', fontSize: 12,
                                          borderBottom: '1px solid var(--border)',
                                          color: 'var(--text-primary)',
                                        }}>
                                          {typeof val === 'boolean' ? (val ? 'Yes' : 'No') :
                                           val instanceof Date || (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}/))
                                             ? new Date(val).toLocaleDateString()
                                             : String(val ?? '-')}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Role Distribution */}
          {auditResult.stats.role_distribution && (
            <div style={{ marginTop: 16 }}>
              <Card>
                <CardContent>
                  <div className={styles.settingsSection}>
                    <div className={styles.sectionHeader}>
                      <Users size={20} />
                      <h3>Role Distribution</h3>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {auditResult.stats.role_distribution.map((r: any) => (
                        <div key={r.role} style={{
                          padding: '12px 20px', borderRadius: 8,
                          background: 'var(--bg-secondary)', textAlign: 'center', minWidth: 100,
                        }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>{r.count}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', textTransform: 'capitalize', marginTop: 2 }}>
                            {r.role === 'superadmin' ? 'Super Admin' : r.role}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );

  const handleRewardImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setActionError('Image size must be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setActionError('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setRewardFormData({ ...rewardFormData, image_url: base64 });
    };
    reader.readAsDataURL(file);
  };

  const resetRewardForm = () => {
    setRewardFormData({
      name: '',
      description: '',
      points_cost: '',
      points_cost_max: '',
      image_url: '',
      category: '',
      redirect_url: ''
    });
    setActionError('');
  };

  const handleAddReward = async () => {
    if (!rewardFormData.name || !rewardFormData.points_cost || !rewardFormData.category) {
      setActionError('Please fill in all required fields');
      return;
    }

    const pointsCost = parseInt(rewardFormData.points_cost);
    if (isNaN(pointsCost) || pointsCost < 1) {
      setActionError('Points cost must be at least 1');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      const pointsCostMax = rewardFormData.points_cost_max ? parseInt(rewardFormData.points_cost_max) : undefined;
      if (pointsCostMax !== undefined && pointsCostMax < pointsCost) {
        setActionError('Max Waves must be greater than or equal to Min Waves');
        setActionLoading(false);
        return;
      }

      await rewardsApi.create({
        name: rewardFormData.name,
        description: rewardFormData.description,
        points_cost: pointsCost,
        points_cost_max: pointsCostMax || undefined,
        image_url: rewardFormData.image_url || undefined,
        category: rewardFormData.category,
        redirect_url: rewardFormData.redirect_url || undefined
      });

      setActionSuccess('Reward created successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowAddRewardModal(false);
      resetRewardForm();
      fetchRewards();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to create reward');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditReward = (reward: Reward) => {
    setSelectedReward(reward);
    setRewardFormData({
      name: reward.name,
      description: reward.description || '',
      points_cost: reward.points_cost.toString(),
      points_cost_max: reward.points_cost_max ? reward.points_cost_max.toString() : '',
      image_url: reward.image_url || '',
      category: reward.category,
      redirect_url: reward.redirect_url || ''
    });
    setActionError('');
    setShowEditRewardModal(true);
  };

  const handleUpdateReward = async () => {
    if (!selectedReward) return;

    if (!rewardFormData.name || !rewardFormData.points_cost || !rewardFormData.category) {
      setActionError('Please fill in all required fields');
      return;
    }

    const pointsCost = parseInt(rewardFormData.points_cost);
    if (isNaN(pointsCost) || pointsCost < 1) {
      setActionError('Points cost must be at least 1');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      const pointsCostMax = rewardFormData.points_cost_max ? parseInt(rewardFormData.points_cost_max) : undefined;
      if (pointsCostMax !== undefined && pointsCostMax < pointsCost) {
        setActionError('Max Waves must be greater than or equal to Min Waves');
        setActionLoading(false);
        return;
      }

      await rewardsApi.update(selectedReward.id, {
        name: rewardFormData.name,
        description: rewardFormData.description,
        points_cost: pointsCost,
        points_cost_max: pointsCostMax || undefined,
        image_url: rewardFormData.image_url || undefined,
        category: rewardFormData.category,
        redirect_url: rewardFormData.redirect_url || undefined
      });

      setActionSuccess('Reward updated successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowEditRewardModal(false);
      resetRewardForm();
      fetchRewards();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to update reward');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteReward = async () => {
    if (!selectedReward) return;

    setActionLoading(true);
    setActionError('');

    try {
      await rewardsApi.delete(selectedReward.id);
      setActionSuccess('Reward deleted successfully!');
      setTimeout(() => setActionSuccess(''), 3000);
      setShowDeleteRewardModal(false);
      setSelectedReward(null);
      fetchRewards();
    } catch (error: any) {
      setActionError(error.response?.data?.error || 'Failed to delete reward');
    } finally {
      setActionLoading(false);
    }
  };

  const renderRippleTypes = () => (
    <div className={styles.tabContent}>
      <div className={styles.contentHeader}>
        <div>
          <h2>Ripple Types</h2>
          <p>Define the types of appreciations that can be sent</p>
        </div>
        <Button onClick={() => { resetRippleTypeForm(); setShowAddRippleTypeModal(true); }}>
          <Plus size={18} /> Add Ripple Type
        </Button>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading ripple types...</p>
        </div>
      ) : rippleTypes.length === 0 ? (
        <Card>
          <CardContent>
            <div className={styles.emptyState}>
              <Heart size={48} />
              <h3>No ripple types defined</h3>
              <p>Add your first ripple type to get started</p>
              <Button onClick={() => { resetRippleTypeForm(); setShowAddRippleTypeModal(true); }}>
                <Plus size={18} /> Add Ripple Type
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className={styles.rippleTypesGrid}>
          {rippleTypes.map((rippleType) => (
            <Card key={rippleType.id} className={`${styles.rippleTypeCard} ${!rippleType.is_active ? styles.inactive : ''}`}>
              <CardContent>
                <div className={styles.rippleTypeHeader}>
                  <div
                    className={styles.rippleTypeIcon}
                    style={{ background: rippleType.color + '20', color: rippleType.color }}
                  >
                    {rippleType.icon}
                  </div>
                  <div className={styles.rippleTypeActions}>
                    <button
                      className={styles.actionBtn}
                      title={rippleType.is_active ? 'Deactivate' : 'Activate'}
                      onClick={() => handleToggleRippleTypeActive(rippleType)}
                    >
                      {rippleType.is_active ? <ToggleRight size={16} className={styles.toggleOn} /> : <ToggleLeft size={16} />}
                    </button>
                    <button
                      className={styles.actionBtn}
                      title="Edit"
                      onClick={() => handleEditRippleType(rippleType)}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className={styles.actionBtn}
                      title="Delete"
                      onClick={() => { setSelectedRippleType(rippleType); setShowDeleteRippleTypeModal(true); }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <h3 className={styles.rippleTypeName}>{rippleType.name}</h3>
                <span className={styles.rippleTypeKey}>{rippleType.key}</span>
                <p className={styles.rippleTypeDescription}>{rippleType.description || 'No description'}</p>
                <div className={styles.rippleTypeMeta}>
                  <span className={styles.rippleTypePoints}>
                    <Star size={14} />
                    {rippleType.default_points} waves default
                  </span>
                  <Badge variant={rippleType.is_active ? 'success' : 'default'} size="sm">
                    {rippleType.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'navigation': return <Globe size={16} />;
      case 'core': return <Heart size={16} />;
      case 'communication': return <Bell size={16} />;
      case 'rewards': return <Gift size={16} />;
      case 'analytics': return <Star size={16} />;
      case 'admin': return <Shield size={16} />;
      default: return <Award size={16} />;
    }
  };

  const groupedPermissions = permissionsMatrix.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {} as { [category: string]: PermissionMatrix[] });

  const renderPermissions = () => (
    <div className={styles.tabContent}>
      <div className={styles.contentHeader}>
        <div>
          <h2>Role Permissions</h2>
          <p>Configure feature access for each role</p>
        </div>
      </div>

      {/* Legend */}
      <div className={styles.permissionsLegend}>
        <div className={styles.legendItem}>
          <span className={`${styles.legendIcon} ${styles.view}`}>V</span>
          <span>View</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendIcon} ${styles.create}`}>C</span>
          <span>Create</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendIcon} ${styles.edit}`}>E</span>
          <span>Edit</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendIcon} ${styles.delete}`}>D</span>
          <span>Delete</span>
        </div>
      </div>

      {/* Role Tabs */}
      <div className={styles.roleTabsContainer}>
        {['admin', 'executive', 'mentor', 'associate'].map(role => (
          <button
            key={role}
            className={`${styles.roleTab} ${selectedPermissionRole === role ? styles.active : ''}`}
            onClick={() => setSelectedPermissionRole(role)}
          >
            {role.charAt(0).toUpperCase() + role.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading permissions...</p>
        </div>
      ) : (
        <div className={styles.permissionsContainer}>
          {Object.entries(groupedPermissions).map(([category, features]) => (
            <div key={category} className={styles.permissionsCategorySection}>
              <div className={styles.permissionsCategoryHeader}>
                {getCategoryIcon(category)}
                <h3>{category}</h3>
              </div>
              <table className={styles.permissionsTable}>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className={styles.roleColumnHeader}>View</th>
                    <th className={styles.roleColumnHeader}>Create</th>
                    <th className={styles.roleColumnHeader}>Edit</th>
                    <th className={styles.roleColumnHeader}>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map(feature => {
                    const perms = feature.permissions[selectedPermissionRole] || {
                      can_view: true,
                      can_create: false,
                      can_edit: false,
                      can_delete: false
                    };
                    return (
                      <tr key={feature.feature_id}>
                        <td>
                          <div className={styles.featureNameCell}>
                            <span className={styles.featureName}>{feature.feature_name}</span>
                            <span className={styles.featureDescription}>{feature.feature_description}</span>
                          </div>
                        </td>
                        <td className={styles.rolePermissionCell}>
                          <button
                            className={`${styles.permIndicator} ${perms.can_view ? styles.view : styles.inactive}`}
                            onClick={() => handlePermissionChange(feature.feature_id, selectedPermissionRole, 'can_view', !perms.can_view)}
                            title="Toggle View Permission"
                          >
                            {perms.can_view ? <Check size={12} /> : 'V'}
                          </button>
                        </td>
                        <td className={styles.rolePermissionCell}>
                          <button
                            className={`${styles.permIndicator} ${perms.can_create ? styles.create : styles.inactive}`}
                            onClick={() => handlePermissionChange(feature.feature_id, selectedPermissionRole, 'can_create', !perms.can_create)}
                            title="Toggle Create Permission"
                          >
                            {perms.can_create ? <Check size={12} /> : 'C'}
                          </button>
                        </td>
                        <td className={styles.rolePermissionCell}>
                          <button
                            className={`${styles.permIndicator} ${perms.can_edit ? styles.edit : styles.inactive}`}
                            onClick={() => handlePermissionChange(feature.feature_id, selectedPermissionRole, 'can_edit', !perms.can_edit)}
                            title="Toggle Edit Permission"
                          >
                            {perms.can_edit ? <Check size={12} /> : 'E'}
                          </button>
                        </td>
                        <td className={styles.rolePermissionCell}>
                          <button
                            className={`${styles.permIndicator} ${perms.can_delete ? styles.delete : styles.inactive}`}
                            onClick={() => handlePermissionChange(feature.feature_id, selectedPermissionRole, 'can_delete', !perms.can_delete)}
                            title="Toggle Delete Permission"
                          >
                            {perms.can_delete ? <Check size={12} /> : 'D'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderRewards = () => {
    const filteredRewards = rewards.filter(r => {
      if (!rewardSearchTerm.trim()) return true;
      const term = rewardSearchTerm.toLowerCase();
      return r.name.toLowerCase().includes(term) ||
        r.description?.toLowerCase().includes(term) ||
        r.category?.toLowerCase().includes(term);
    });

    return (
    <div className={styles.tabContent}>
      <div className={styles.contentHeader}>
        <div>
          <h2>Rewards Management</h2>
          <p>Create and manage rewards for the perks catalog</p>
        </div>
        <Button onClick={() => { resetRewardForm(); setShowAddRewardModal(true); }}>
          <Plus size={18} /> Add Reward
        </Button>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading rewards...</p>
        </div>
      ) : rewards.length === 0 ? (
        <Card>
          <CardContent>
            <div className={styles.emptyState}>
              <Gift size={48} />
              <h3>No rewards created</h3>
              <p>Add your first reward to get started</p>
              <Button onClick={() => { resetRewardForm(); setShowAddRewardModal(true); }}>
                <Plus size={18} /> Add Reward
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className={styles.searchBox} style={{ marginBottom: 20 }}>
          <Search size={18} />
          <input
            type="text"
            placeholder="Search rewards..."
            value={rewardSearchTerm}
            onChange={(e) => setRewardSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.rewardsGrid}>
          {filteredRewards.map((reward) => (
            <Card key={reward.id} className={styles.rewardCard}>
              <CardContent>
                <div className={styles.rewardImageContainer}>
                  {reward.image_url ? (
                    <img src={reward.image_url} alt={reward.name} className={styles.rewardImage} />
                  ) : (
                    <div className={styles.rewardImagePlaceholder}>
                      <Gift size={48} />
                    </div>
                  )}
                </div>
                <div className={styles.rewardContent}>
                  <Badge variant="default" size="sm">{reward.category}</Badge>
                  <h3 className={styles.rewardName}>{reward.name}</h3>
                  <p className={styles.rewardDescription}>{reward.description}</p>
                  <div className={styles.rewardMeta}>
                    <span className={styles.rewardPoints}>
                      <Star size={16} />
                      {reward.points_cost_max && reward.points_cost_max !== reward.points_cost
                        ? `${reward.points_cost} - ${reward.points_cost_max} waves`
                        : `${reward.points_cost} waves`}
                    </span>
                  </div>
                  <div className={styles.rewardActions}>
                    <Button variant="outline" size="sm" onClick={() => handleEditReward(reward)}>
                      <Edit2 size={14} /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedReward(reward); setShowDeleteRewardModal(true); }}>
                      <Trash2 size={14} /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        </>
      )}
    </div>
  );
  };

  // Role guard: only admin and superadmin can access Settings
  if (currentUser && currentUser.role !== 'superadmin' && currentUser.role !== 'admin') {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Access Denied</h1>
          <p>You do not have permission to access Settings. Only administrators can view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Settings</h1>
        <p>Manage your organization settings and preferences</p>
      </div>

      <div className={styles.layout}>
        <div className={styles.sidebar}>
          <nav className={styles.nav}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.navItem} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={18} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className={styles.content}>
          {activeTab === 'users' && renderUserManagement()}
          {activeTab === 'points' && renderPoints()}
          {activeTab === 'rewards' && renderRewards()}
          {activeTab === 'values' && renderCompanyValues()}
          {activeTab === 'ripple-types' && renderRippleTypes()}
          {activeTab === 'permissions' && renderPermissions()}
          {activeTab === 'integrations' && renderIntegrations()}
          {activeTab === 'events' && renderEventsManagement()}
          {activeTab === 'security' && renderSecurity()}
        </div>
      </div>

      {/* Xoxoday Token Update Modal */}
      <FormModal
        isOpen={showXoxodayTokenModal}
        onClose={() => {
          setShowXoxodayTokenModal(false);
          setXoxodayTokenForm({ client_id: '', client_secret: '', access_token: '', refresh_token: '' });
          setActionError('');
        }}
        title="Update Xoxoday Tokens"
        subtitle="Manage Xoxoday API credentials"
        icon={<Key size={16} />}
        onSubmit={handleUpdateXoxodayTokens}
        submitLabel="Update Tokens"
        submitLoadingLabel="Updating…"
        submitLoading={actionLoading}
        footerHint={<><AlertCircle size={12} /> Only fill the fields you want to update</>}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
        {actionSuccess && <div className={styles.successMessage}>{actionSuccess}</div>}

        <p className={styles.modalDescription}>
          Enter your Xoxoday API credentials. You can get these from the Xoxoday dashboard under
          Settings → API → Storefront Integration.
        </p>

        <div className={styles.formGroup}>
            <label>Client ID</label>
            <input
              type="text"
              placeholder="Enter Client ID"
              value={xoxodayTokenForm.client_id}
              onChange={(e) => setXoxodayTokenForm({ ...xoxodayTokenForm, client_id: e.target.value })}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Client Secret</label>
            <input
              type="password"
              placeholder="Enter Client Secret"
              value={xoxodayTokenForm.client_secret}
              onChange={(e) => setXoxodayTokenForm({ ...xoxodayTokenForm, client_secret: e.target.value })}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Access Token</label>
            <textarea
              placeholder="Paste Access Token"
              value={xoxodayTokenForm.access_token}
              onChange={(e) => setXoxodayTokenForm({ ...xoxodayTokenForm, access_token: e.target.value })}
              rows={3}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Refresh Token</label>
            <textarea
              placeholder="Paste Refresh Token"
              value={xoxodayTokenForm.refresh_token}
              onChange={(e) => setXoxodayTokenForm({ ...xoxodayTokenForm, refresh_token: e.target.value })}
              rows={3}
            />
          </div>

      </FormModal>

      <FormModal
        isOpen={showAddUserModal}
        onClose={() => { setShowAddUserModal(false); resetAddUserForm(); }}
        title="Add New User"
        subtitle="Create a new account for a team member"
        icon={<UserPlus size={16} />}
        onSubmit={handleAddUser}
        submitLabel="Add User"
        submitLoadingLabel="Adding…"
        submitLoading={actionLoading}
        footerHint={<><Mail size={12} /> Credentials will be emailed to the user</>}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>First Name *</label>
              <input
                type="text"
                placeholder="Enter first name"
                value={addUserFormData.first_name}
                onChange={(e) => setAddUserFormData({ ...addUserFormData, first_name: e.target.value })}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Last Name *</label>
              <input
                type="text"
                placeholder="Enter last name"
                value={addUserFormData.last_name}
                onChange={(e) => setAddUserFormData({ ...addUserFormData, last_name: e.target.value })}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Email *</label>
            <input
              type="email"
              placeholder="Enter email address"
              value={addUserFormData.email}
              onChange={(e) => setAddUserFormData({ ...addUserFormData, email: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Mobile Number *</label>
            <input
              type="tel"
              placeholder="Enter mobile number"
              value={addUserFormData.phone}
              onChange={(e) => setAddUserFormData({ ...addUserFormData, phone: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Password *</label>
            <input
              type="password"
              placeholder="Enter password (min 8 characters)"
              value={addUserFormData.password}
              onChange={(e) => setAddUserFormData({ ...addUserFormData, password: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Role</label>
            <select
              value={addUserFormData.role}
              onChange={(e) => setAddUserFormData({ ...addUserFormData, role: e.target.value })}
            >
              <option value="associate">Associate</option>
              <option value="mentor">Mentor</option>
              <option value="executive">Executive</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Mentor</label>
            <select
              value={addUserFormData.mentor_id}
              onChange={(e) => setAddUserFormData({ ...addUserFormData, mentor_id: e.target.value })}
            >
              <option value="">Select Mentor (Optional)</option>
              {users.filter(u => u.is_active).map((mentor) => (
                <option key={mentor.id} value={mentor.id}>
                  {mentor.first_name} {mentor.last_name}
                </option>
              ))}
            </select>
          </div>
      </FormModal>

      {/* Add Company Value Modal */}
      <FormModal
        isOpen={showAddValueModal}
        onClose={() => { setShowAddValueModal(false); resetValueForm(); }}
        title="Add Company Value"
        subtitle="Define a core value for your organization"
        icon={<Heart size={16} />}
        onSubmit={handleAddValue}
        submitLabel="Add Value"
        submitLoadingLabel="Creating…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Value Name *</label>
            <input
              type="text"
              placeholder="e.g., Innovation, Teamwork"
              value={valueFormData.name}
              onChange={(e) => setValueFormData({ ...valueFormData, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Description *</label>
            <textarea
              placeholder="Describe what this value means to your organization"
              rows={3}
              className={styles.textarea}
              value={valueFormData.description}
              onChange={(e) => setValueFormData({ ...valueFormData, description: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Icon</label>
            <div className={styles.iconPickerGrid}>
              {ICON_OPTIONS.map(({ name, Icon }) => (
                <button
                  key={name}
                  type="button"
                  className={`${styles.iconPickerBtn} ${valueFormData.icon === name && !valueFormData.icon_url ? styles.iconPickerBtnActive : ''}`}
                  onClick={() => setValueFormData({ ...valueFormData, icon: name, icon_url: '' })}
                  title={name}
                  style={valueFormData.icon === name && !valueFormData.icon_url ? { borderColor: valueFormData.color, color: valueFormData.color, background: valueFormData.color + '12' } : undefined}
                >
                  <Icon size={20} />
                </button>
              ))}
            </div>
            <div className={styles.iconUploadRow}>
              <span className={styles.iconUploadLabel}>or upload custom:</span>
              <input
                id="addValueIconInput"
                type="file"
                accept="image/*"
                onChange={handleValueIconUpload}
                style={{ display: 'none' }}
              />
              {valueFormData.icon_url ? (
                <div className={styles.iconPreview}>
                  <img src={valueFormData.icon_url} alt="Icon preview" />
                  <button
                    type="button"
                    className={styles.removeIconBtn}
                    onClick={() => setValueFormData({ ...valueFormData, icon_url: '', icon: valueFormData.icon || 'star' })}
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.uploadIconBtn}
                  onClick={() => document.getElementById('addValueIconInput')?.click()}
                >
                  <Upload size={16} /> Upload
                </button>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Color</label>
            <input
              type="color"
              className={styles.colorInput}
              value={valueFormData.color}
              onChange={(e) => setValueFormData({ ...valueFormData, color: e.target.value })}
            />
          </div>
      </FormModal>

      {/* Edit Company Value Modal */}
      <FormModal
        isOpen={showEditValueModal}
        onClose={() => { setShowEditValueModal(false); resetValueForm(); setSelectedValue(null); }}
        title="Edit Company Value"
        subtitle="Update this value's details or visual"
        icon={<Edit2 size={16} />}
        onSubmit={handleUpdateValue}
        submitLabel="Save Changes"
        submitLoadingLabel="Saving…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Value Name *</label>
            <input
              type="text"
              placeholder="e.g., Innovation, Teamwork"
              value={valueFormData.name}
              onChange={(e) => setValueFormData({ ...valueFormData, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Description *</label>
            <textarea
              placeholder="Describe what this value means to your organization"
              rows={3}
              className={styles.textarea}
              value={valueFormData.description}
              onChange={(e) => setValueFormData({ ...valueFormData, description: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Icon</label>
            <div className={styles.iconPickerGrid}>
              {ICON_OPTIONS.map(({ name, Icon }) => (
                <button
                  key={name}
                  type="button"
                  className={`${styles.iconPickerBtn} ${valueFormData.icon === name && !valueFormData.icon_url ? styles.iconPickerBtnActive : ''}`}
                  onClick={() => setValueFormData({ ...valueFormData, icon: name, icon_url: '' })}
                  title={name}
                  style={valueFormData.icon === name && !valueFormData.icon_url ? { borderColor: valueFormData.color, color: valueFormData.color, background: valueFormData.color + '12' } : undefined}
                >
                  <Icon size={20} />
                </button>
              ))}
            </div>
            <div className={styles.iconUploadRow}>
              <span className={styles.iconUploadLabel}>or upload custom:</span>
              <input
                id="editValueIconInput"
                type="file"
                accept="image/*"
                onChange={handleValueIconUpload}
                style={{ display: 'none' }}
              />
              {valueFormData.icon_url ? (
                <div className={styles.iconPreview}>
                  <img src={valueFormData.icon_url} alt="Icon preview" />
                  <button
                    type="button"
                    className={styles.removeIconBtn}
                    onClick={() => setValueFormData({ ...valueFormData, icon_url: '', icon: valueFormData.icon || 'star' })}
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.uploadIconBtn}
                  onClick={() => document.getElementById('editValueIconInput')?.click()}
                >
                  <Upload size={16} /> Upload
                </button>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Color</label>
            <input
              type="color"
              className={styles.colorInput}
              value={valueFormData.color}
              onChange={(e) => setValueFormData({ ...valueFormData, color: e.target.value })}
            />
          </div>
      </FormModal>

      {/* Delete Company Value Confirmation Modal */}
      <Modal
        isOpen={showDeleteValueModal}
        onClose={() => { setShowDeleteValueModal(false); setSelectedValue(null); setActionError(''); }}
        title="Delete Company Value"
      >
        <div className={styles.addUserForm}>
          {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.deleteConfirmation}>
            <p>Are you sure you want to delete <strong>{selectedValue?.name}</strong>?</p>
            <p className={styles.deleteWarning}>This action cannot be undone.</p>
          </div>
          <div className={styles.formActions}>
            <Button variant="ghost" onClick={() => { setShowDeleteValueModal(false); setSelectedValue(null); setActionError(''); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteValue} disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete Value'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <FormModal
        isOpen={showEditUserModal}
        onClose={() => setShowEditUserModal(false)}
        title="Edit User"
        subtitle="Update user details and role"
        icon={<Edit2 size={16} />}
        onSubmit={handleUpdateUser}
        submitLabel="Save Changes"
        submitLoadingLabel="Saving…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>First Name</label>
              <input
                type="text"
                value={editFormData.first_name}
                onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Last Name</label>
              <input
                type="text"
                value={editFormData.last_name}
                onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Email</label>
            <input
              type="email"
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Mobile Number</label>
            <input
              type="tel"
              placeholder="Enter mobile number"
              value={editFormData.phone}
              onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Role</label>
            <select
              value={editFormData.role}
              onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
            >
              <option value="associate">Associate</option>
              <option value="mentor">Mentor</option>
              <option value="executive">Executive</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Mentor</label>
            <select
              value={editFormData.mentor_id}
              onChange={(e) => setEditFormData({ ...editFormData, mentor_id: e.target.value })}
            >
              <option value="">Select Mentor (Optional)</option>
              {users.filter(u => u.is_active && u.id !== selectedUser?.id).map((mentor) => (
                <option key={mentor.id} value={mentor.id}>
                  {mentor.first_name} {mentor.last_name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Status</label>
            <select
              value={editFormData.is_active ? 'active' : 'inactive'}
              onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.value === 'active' })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {currentUser?.role === 'superadmin' && (
            <>
              <div className={styles.formDivider}>
                <span>Event Dates (for auto-generated wishes)</span>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label><Cake size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Birthday</label>
                  <input
                    type="date"
                    value={editFormData.birthday}
                    onChange={(e) => setEditFormData({ ...editFormData, birthday: e.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label><Briefcase size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Joining Date</label>
                  <input
                    type="date"
                    value={editFormData.joining_date}
                    onChange={(e) => setEditFormData({ ...editFormData, joining_date: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}
      </FormModal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        title="Remove User"
      >
        <div className={styles.addUserForm}>
          {actionError && <div className={styles.errorMessage}>{actionError}</div>}

          <div className={styles.deleteConfirmation}>
            <p>How would you like to remove <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong>?</p>
          </div>

          <div className={styles.deleteOptions}>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name="deleteType"
                checked={deleteOptions.deleteType === 'deactivate'}
                onChange={() => setDeleteOptions({ ...deleteOptions, deleteType: 'deactivate', removeContributions: false })}
              />
              <div className={styles.radioContent}>
                <span className={styles.radioLabel}>Deactivate User</span>
                <span className={styles.radioDesc}>User cannot login but can be reactivated later. All data is preserved.</span>
              </div>
            </label>

            <label className={styles.radioOption}>
              <input
                type="radio"
                name="deleteType"
                checked={deleteOptions.deleteType === 'permanent'}
                onChange={() => setDeleteOptions({ ...deleteOptions, deleteType: 'permanent' })}
              />
              <div className={styles.radioContent}>
                <span className={styles.radioLabel}>Permanently Delete User</span>
                <span className={styles.radioDesc}>User will be permanently removed from the organization.</span>
              </div>
            </label>
          </div>

          {deleteOptions.deleteType === 'permanent' && (
            <div className={styles.contributionOptions}>
              <p className={styles.contributionLabel}>What should happen to the user's contributions?</p>

              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="contributions"
                  checked={!deleteOptions.removeContributions}
                  onChange={() => setDeleteOptions({ ...deleteOptions, removeContributions: false })}
                />
                <div className={styles.radioContent}>
                  <span className={styles.radioLabel}>Keep Contributions</span>
                  <span className={styles.radioDesc}>Appreciations, comments, and reactions will be shown as "Deleted User"</span>
                </div>
              </label>

              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="contributions"
                  checked={deleteOptions.removeContributions}
                  onChange={() => setDeleteOptions({ ...deleteOptions, removeContributions: true })}
                />
                <div className={styles.radioContent}>
                  <span className={styles.radioLabel}>Remove All Contributions</span>
                  <span className={styles.radioDesc}>All appreciations, comments, and reactions by this user will be permanently deleted</span>
                </div>
              </label>

              {deleteOptions.removeContributions && (
                <div className={styles.severeWarning}>
                  ⚠️ Warning: This will permanently delete all appreciations given and received by this user, along with all their comments and reactions. This action cannot be undone!
                </div>
              )}
            </div>
          )}

          <div className={styles.formActions}>
            <Button variant="ghost" onClick={() => setShowDeleteConfirmModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteUser}
              disabled={actionLoading}
            >
              {actionLoading
                ? 'Processing...'
                : deleteOptions.deleteType === 'deactivate'
                  ? 'Deactivate User'
                  : deleteOptions.removeContributions
                    ? 'Delete User & Contributions'
                    : 'Delete User'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Waves Management Modal (Superadmin only) */}
      <FormModal
        isOpen={showManagePointsModal}
        onClose={() => { setShowManagePointsModal(false); setManagePointsData({ user_id: '', amount: '', operation: 'add', reason: '' }); setActionError(''); }}
        title="Manage User Waves"
        subtitle="Add or reduce waves for a specific user"
        icon={<Coins size={16} />}
        onSubmit={handleManagePoints}
        submitLabel={managePointsData.operation === 'add' ? 'Add Waves' : 'Reduce Waves'}
        submitLoadingLabel="Processing…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Select User *</label>
            <select
              value={managePointsData.user_id}
              onChange={(e) => setManagePointsData({ ...managePointsData, user_id: e.target.value })}
            >
              <option value="">Choose a user...</option>
              {users.filter(u => u.is_active).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} ({u.email}) - Current: {u.points_balance} waves
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Operation *</label>
            <select
              value={managePointsData.operation}
              onChange={(e) => setManagePointsData({ ...managePointsData, operation: e.target.value as 'add' | 'reduce' })}
            >
              <option value="add">Add Waves</option>
              <option value="reduce">Reduce Waves</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Amount *</label>
            <input
              type="number"
              placeholder="Enter waves amount"
              min="1"
              value={managePointsData.amount}
              onChange={(e) => setManagePointsData({ ...managePointsData, amount: e.target.value })}
            />
          </div>
          {managePointsData.operation === 'reduce' && (
            <div className={styles.severeWarning}>
              Note: Reduced waves will be transferred to your (superadmin) account.
            </div>
          )}
          <div className={styles.formGroup}>
            <label>Reason (Optional)</label>
            <input
              type="text"
              placeholder={managePointsData.operation === 'add' ? 'e.g., Quarterly bonus, Performance reward' : 'e.g., Policy violation, Account adjustment'}
              value={managePointsData.reason}
              onChange={(e) => setManagePointsData({ ...managePointsData, reason: e.target.value })}
            />
          </div>
      </FormModal>

      {/* Reset Password Modal */}
      <FormModal
        isOpen={showResetPasswordModal}
        onClose={() => { setShowResetPasswordModal(false); setResetPasswordData({ new_password: '', confirm_password: '' }); setActionError(''); }}
        title="Reset Password"
        subtitle={selectedUser ? `For ${selectedUser.first_name} ${selectedUser.last_name}` : 'Set a new password'}
        icon={<Lock size={16} />}
        onSubmit={handleResetPassword}
        submitLabel="Reset Password"
        submitLoadingLabel="Resetting…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <p className={styles.resetPasswordInfo}>
            Reset password for <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong> ({selectedUser?.email})
          </p>
          <div className={styles.formGroup}>
            <label>New Password *</label>
            <input
              type="password"
              placeholder="Enter new password (min 8 characters)"
              value={resetPasswordData.new_password}
              onChange={(e) => setResetPasswordData({ ...resetPasswordData, new_password: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Confirm Password *</label>
            <input
              type="password"
              placeholder="Confirm new password"
              value={resetPasswordData.confirm_password}
              onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirm_password: e.target.value })}
            />
          </div>
      </FormModal>

      {/* Add Reward Modal */}
      <FormModal
        isOpen={showAddRewardModal}
        onClose={() => { setShowAddRewardModal(false); resetRewardForm(); }}
        title="Add New Reward"
        subtitle="Add a new item to the rewards catalog"
        icon={<Gift size={16} />}
        onSubmit={handleAddReward}
        submitLabel="Add Reward"
        submitLoadingLabel="Adding…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Name *</label>
            <input
              type="text"
              placeholder="Enter reward name"
              value={rewardFormData.name}
              onChange={(e) => setRewardFormData({ ...rewardFormData, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              placeholder="Enter reward description"
              rows={3}
              className={styles.textarea}
              value={rewardFormData.description}
              onChange={(e) => setRewardFormData({ ...rewardFormData, description: e.target.value })}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Min Waves *</label>
              <input
                type="number"
                placeholder="e.g., 100"
                min="1"
                value={rewardFormData.points_cost}
                onChange={(e) => setRewardFormData({ ...rewardFormData, points_cost: e.target.value })}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Max Waves</label>
              <input
                type="number"
                placeholder="Optional"
                min="1"
                value={rewardFormData.points_cost_max}
                onChange={(e) => setRewardFormData({ ...rewardFormData, points_cost_max: e.target.value })}
              />
              <span className={styles.fieldHint}>Leave empty for a single value</span>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Category *</label>
            <input
              type="text"
              placeholder="e.g., Gift Cards, Experiences, Merchandise"
              value={rewardFormData.category}
              onChange={(e) => setRewardFormData({ ...rewardFormData, category: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Xoxoday Redirect Link</label>
            <input
              type="text"
              placeholder="e.g., https://stores.xoxoday.com/matasmaxoxo/vouchers/description/..."
              value={rewardFormData.redirect_url}
              onChange={(e) => setRewardFormData({ ...rewardFormData, redirect_url: e.target.value })}
            />
            <span className={styles.fieldHint}>Optional: Paste the full Xoxoday storefront link for this voucher</span>
          </div>
          <div className={styles.formGroup}>
            <label>Reward Image</label>
            <div className={styles.imageUploadArea} onClick={() => rewardImageInputRef.current?.click()}>
              {rewardFormData.image_url ? (
                <div className={styles.imagePreview}>
                  <img src={rewardFormData.image_url} alt="Preview" />
                  <button
                    type="button"
                    className={styles.removeImageBtn}
                    onClick={(e) => { e.stopPropagation(); setRewardFormData({ ...rewardFormData, image_url: '' }); }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <Upload size={32} />
                  <span>Click to upload image</span>
                  <span className={styles.uploadHint}>Max 5MB, JPG/PNG</span>
                </div>
              )}
            </div>
            <input
              ref={rewardImageInputRef}
              type="file"
              accept="image/*"
              onChange={handleRewardImageUpload}
              style={{ display: 'none' }}
            />
          </div>
      </FormModal>

      {/* Edit Reward Modal */}
      <FormModal
        isOpen={showEditRewardModal}
        onClose={() => { setShowEditRewardModal(false); resetRewardForm(); }}
        title="Edit Reward"
        subtitle="Update reward details"
        icon={<Edit2 size={16} />}
        onSubmit={handleUpdateReward}
        submitLabel="Save Changes"
        submitLoadingLabel="Saving…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Name *</label>
            <input
              type="text"
              placeholder="Enter reward name"
              value={rewardFormData.name}
              onChange={(e) => setRewardFormData({ ...rewardFormData, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              placeholder="Enter reward description"
              rows={3}
              className={styles.textarea}
              value={rewardFormData.description}
              onChange={(e) => setRewardFormData({ ...rewardFormData, description: e.target.value })}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Min Waves *</label>
              <input
                type="number"
                placeholder="e.g., 100"
                min="1"
                value={rewardFormData.points_cost}
                onChange={(e) => setRewardFormData({ ...rewardFormData, points_cost: e.target.value })}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Max Waves</label>
              <input
                type="number"
                placeholder="Optional"
                min="1"
                value={rewardFormData.points_cost_max}
                onChange={(e) => setRewardFormData({ ...rewardFormData, points_cost_max: e.target.value })}
              />
              <span className={styles.fieldHint}>Leave empty for a single value</span>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Category *</label>
            <input
              type="text"
              placeholder="e.g., Gift Cards, Experiences, Merchandise"
              value={rewardFormData.category}
              onChange={(e) => setRewardFormData({ ...rewardFormData, category: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Xoxoday Redirect Link</label>
            <input
              type="text"
              placeholder="e.g., https://stores.xoxoday.com/matasmaxoxo/vouchers/description/..."
              value={rewardFormData.redirect_url}
              onChange={(e) => setRewardFormData({ ...rewardFormData, redirect_url: e.target.value })}
            />
            <span className={styles.fieldHint}>Optional: Paste the full Xoxoday storefront link for this voucher</span>
          </div>
          <div className={styles.formGroup}>
            <label>Reward Image</label>
            <div className={styles.imageUploadArea} onClick={() => rewardImageInputRef.current?.click()}>
              {rewardFormData.image_url ? (
                <div className={styles.imagePreview}>
                  <img src={rewardFormData.image_url} alt="Preview" />
                  <button
                    type="button"
                    className={styles.removeImageBtn}
                    onClick={(e) => { e.stopPropagation(); setRewardFormData({ ...rewardFormData, image_url: '' }); }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <Upload size={32} />
                  <span>Click to upload image</span>
                  <span className={styles.uploadHint}>Max 5MB, JPG/PNG</span>
                </div>
              )}
            </div>
            <input
              ref={rewardImageInputRef}
              type="file"
              accept="image/*"
              onChange={handleRewardImageUpload}
              style={{ display: 'none' }}
            />
          </div>
      </FormModal>

      {/* Delete Reward Confirmation Modal */}
      <Modal
        isOpen={showDeleteRewardModal}
        onClose={() => { setShowDeleteRewardModal(false); setSelectedReward(null); setActionError(''); }}
        title="Delete Reward"
      >
        <div className={styles.addUserForm}>
          {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.deleteConfirmation}>
            <p>Are you sure you want to delete <strong>{selectedReward?.name}</strong>?</p>
            <p className={styles.deleteWarning}>This action cannot be undone.</p>
          </div>
          <div className={styles.formActions}>
            <Button variant="ghost" onClick={() => { setShowDeleteRewardModal(false); setSelectedReward(null); setActionError(''); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteReward} disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete Reward'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Ripple Type Modal */}
      <FormModal
        isOpen={showAddRippleTypeModal}
        onClose={() => { setShowAddRippleTypeModal(false); resetRippleTypeForm(); }}
        title="Add Ripple Type"
        subtitle="Create a new appreciation category"
        icon={<Sparkles size={16} />}
        onSubmit={handleAddRippleType}
        submitLabel="Create Type"
        submitLoadingLabel="Creating…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Name *</label>
            <input
              type="text"
              placeholder="e.g., Kudos, Great Job"
              value={rippleTypeFormData.name}
              onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Key * (unique identifier)</label>
            <input
              type="text"
              placeholder="e.g., kudos, great_job"
              value={rippleTypeFormData.key}
              onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              placeholder="Describe this ripple type"
              rows={3}
              className={styles.textarea}
              value={rippleTypeFormData.description}
              onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, description: e.target.value })}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Icon (emoji)</label>
              <input
                type="text"
                placeholder="e.g., 👏, ⭐, 🎉"
                maxLength={2}
                value={rippleTypeFormData.icon}
                onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, icon: e.target.value })}
                className={styles.emojiInput}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Color</label>
              <input
                type="color"
                className={styles.colorInput}
                value={rippleTypeFormData.color}
                onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, color: e.target.value })}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Default Waves</label>
            <input
              type="number"
              placeholder="10"
              min="1"
              value={rippleTypeFormData.default_points}
              onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, default_points: e.target.value })}
            />
          </div>
      </FormModal>

      {/* Edit Ripple Type Modal */}
      <FormModal
        isOpen={showEditRippleTypeModal}
        onClose={() => { setShowEditRippleTypeModal(false); resetRippleTypeForm(); setSelectedRippleType(null); }}
        title="Edit Ripple Type"
        subtitle="Update appreciation category details"
        icon={<Edit2 size={16} />}
        onSubmit={handleUpdateRippleType}
        submitLabel="Save Changes"
        submitLoadingLabel="Saving…"
        submitLoading={actionLoading}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Name *</label>
            <input
              type="text"
              placeholder="e.g., Kudos, Great Job"
              value={rippleTypeFormData.name}
              onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Key * (unique identifier)</label>
            <input
              type="text"
              placeholder="e.g., kudos, great_job"
              value={rippleTypeFormData.key}
              onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              placeholder="Describe this ripple type"
              rows={3}
              className={styles.textarea}
              value={rippleTypeFormData.description}
              onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, description: e.target.value })}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Icon (emoji)</label>
              <input
                type="text"
                placeholder="e.g., 👏, ⭐, 🎉"
                maxLength={2}
                value={rippleTypeFormData.icon}
                onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, icon: e.target.value })}
                className={styles.emojiInput}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Color</label>
              <input
                type="color"
                className={styles.colorInput}
                value={rippleTypeFormData.color}
                onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, color: e.target.value })}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Default Waves</label>
            <input
              type="number"
              placeholder="10"
              min="1"
              value={rippleTypeFormData.default_points}
              onChange={(e) => setRippleTypeFormData({ ...rippleTypeFormData, default_points: e.target.value })}
            />
          </div>
      </FormModal>

      {/* Delete Ripple Type Confirmation Modal */}
      <Modal
        isOpen={showDeleteRippleTypeModal}
        onClose={() => { setShowDeleteRippleTypeModal(false); setSelectedRippleType(null); setActionError(''); }}
        title="Delete Ripple Type"
      >
        <div className={styles.addUserForm}>
          {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.deleteConfirmation}>
            <p>Are you sure you want to delete <strong>{selectedRippleType?.name}</strong>?</p>
            <p className={styles.deleteWarning}>This ripple type will no longer be available for sending appreciations.</p>
          </div>
          <div className={styles.formActions}>
            <Button variant="ghost" onClick={() => { setShowDeleteRippleTypeModal(false); setSelectedRippleType(null); setActionError(''); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteRippleType} disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete Ripple Type'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Auto-Waves Rule Modal */}
      <FormModal
        isOpen={showAddAutoWavesModal}
        onClose={() => { setShowAddAutoWavesModal(false); resetAutoWavesForm(); }}
        title="Add Auto-Waves Rule"
        subtitle="Automate waves based on triggers and schedules"
        icon={<Repeat size={16} />}
        onSubmit={handleAddAutoWavesRule}
        submitLabel="Create Rule"
        submitLoadingLabel="Creating…"
        submitLoading={actionLoading}
      >
        <div>
          {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Rule Name *</label>
            <input
              type="text"
              placeholder="e.g., Monthly Team Bonus"
              value={autoWavesFormData.name}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              placeholder="Describe what this rule does"
              rows={2}
              className={styles.textarea}
              value={autoWavesFormData.description}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, description: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Rule Type *</label>
            <select
              value={autoWavesFormData.rule_type}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, rule_type: e.target.value as any })}
            >
              <option value="recurring">Recurring Schedule</option>
              <option value="birthday">Birthday</option>
              <option value="work_anniversary">Work Anniversary</option>
            </select>
          </div>
          {autoWavesFormData.rule_type === 'recurring' && (
            <>
              <div className={styles.formGroup}>
                <label>Frequency *</label>
                <select
                  value={autoWavesFormData.frequency}
                  onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, frequency: e.target.value as any })}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              {autoWavesFormData.frequency === 'weekly' && (
                <div className={styles.formGroup}>
                  <label>Day of Week</label>
                  <select
                    value={autoWavesFormData.day_of_week}
                    onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, day_of_week: parseInt(e.target.value) })}
                  >
                    <option value={0}>Sunday</option>
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                  </select>
                </div>
              )}
              {['monthly', 'quarterly', 'yearly'].includes(autoWavesFormData.frequency) && (
                <div className={styles.formGroup}>
                  <label>Day of Month</label>
                  <input
                    type="number"
                    min={1}
                    max={28}
                    value={autoWavesFormData.day_of_month}
                    onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, day_of_month: parseInt(e.target.value) })}
                  />
                </div>
              )}
            </>
          )}
          <div className={styles.formGroup}>
            <label>Waves Amount *</label>
            <input
              type="number"
              placeholder="100"
              min={1}
              value={autoWavesFormData.waves_amount}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, waves_amount: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Target Users</label>
            <select
              value={autoWavesFormData.target_type}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, target_type: e.target.value as any })}
            >
              <option value="all_active">All Active Users</option>
              <option value="by_role">By Role</option>
            </select>
          </div>
          {autoWavesFormData.target_type === 'by_role' && (
            <div className={styles.formGroup}>
              <label>Select Roles</label>
              <div className={styles.checkboxGroup}>
                {['superadmin', 'admin', 'executive', 'mentor', 'associate'].map((role) => (
                  <label key={role} className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={autoWavesFormData.target_roles.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAutoWavesFormData({ ...autoWavesFormData, target_roles: [...autoWavesFormData.target_roles, role] });
                        } else {
                          setAutoWavesFormData({ ...autoWavesFormData, target_roles: autoWavesFormData.target_roles.filter(r => r !== role) });
                        }
                      }}
                    />
                    {formatRole(role)}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className={styles.formGroup}>
            <label>Message Template</label>
            <input
              type="text"
              placeholder="Auto-credited waves for your contribution"
              value={autoWavesFormData.reason_template}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, reason_template: e.target.value })}
            />
            <span className={styles.fieldHint}>Use {'{{waves_amount}}'} for the amount, {'{{user_name}}'} for recipient name</span>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={autoWavesFormData.is_active}
                onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, is_active: e.target.checked })}
              />
              Enable this rule immediately
            </label>
          </div>
        </div>
      </FormModal>

      {/* Edit Auto-Waves Rule Modal */}
      <FormModal
        isOpen={showEditAutoWavesModal}
        onClose={() => { setShowEditAutoWavesModal(false); setSelectedAutoWavesRule(null); resetAutoWavesForm(); }}
        title="Edit Auto-Waves Rule"
        subtitle="Update rule triggers, schedule, or amount"
        icon={<Edit2 size={16} />}
        onSubmit={handleUpdateAutoWavesRule}
        submitLabel="Save Changes"
        submitLoadingLabel="Saving…"
        submitLoading={actionLoading}
      >
        <div>
          {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Rule Name *</label>
            <input
              type="text"
              placeholder="e.g., Monthly Team Bonus"
              value={autoWavesFormData.name}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, name: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              placeholder="Describe what this rule does"
              rows={2}
              className={styles.textarea}
              value={autoWavesFormData.description}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, description: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Rule Type</label>
            <select
              value={autoWavesFormData.rule_type}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, rule_type: e.target.value as any })}
              disabled={selectedAutoWavesRule?.rule_type !== 'recurring'}
            >
              <option value="recurring">Recurring Schedule</option>
              <option value="birthday">Birthday</option>
              <option value="work_anniversary">Work Anniversary</option>
            </select>
          </div>
          {autoWavesFormData.rule_type === 'recurring' && (
            <>
              <div className={styles.formGroup}>
                <label>Frequency *</label>
                <select
                  value={autoWavesFormData.frequency}
                  onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, frequency: e.target.value as any })}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              {autoWavesFormData.frequency === 'weekly' && (
                <div className={styles.formGroup}>
                  <label>Day of Week</label>
                  <select
                    value={autoWavesFormData.day_of_week}
                    onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, day_of_week: parseInt(e.target.value) })}
                  >
                    <option value={0}>Sunday</option>
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                  </select>
                </div>
              )}
              {['monthly', 'quarterly', 'yearly'].includes(autoWavesFormData.frequency) && (
                <div className={styles.formGroup}>
                  <label>Day of Month</label>
                  <input
                    type="number"
                    min={1}
                    max={28}
                    value={autoWavesFormData.day_of_month}
                    onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, day_of_month: parseInt(e.target.value) })}
                  />
                </div>
              )}
            </>
          )}
          <div className={styles.formGroup}>
            <label>Waves Amount *</label>
            <input
              type="number"
              placeholder="100"
              min={1}
              value={autoWavesFormData.waves_amount}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, waves_amount: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Target Users</label>
            <select
              value={autoWavesFormData.target_type}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, target_type: e.target.value as any })}
            >
              <option value="all_active">All Active Users</option>
              <option value="by_role">By Role</option>
            </select>
          </div>
          {autoWavesFormData.target_type === 'by_role' && (
            <div className={styles.formGroup}>
              <label>Select Roles</label>
              <div className={styles.checkboxGroup}>
                {['superadmin', 'admin', 'executive', 'mentor', 'associate'].map((role) => (
                  <label key={role} className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={autoWavesFormData.target_roles.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAutoWavesFormData({ ...autoWavesFormData, target_roles: [...autoWavesFormData.target_roles, role] });
                        } else {
                          setAutoWavesFormData({ ...autoWavesFormData, target_roles: autoWavesFormData.target_roles.filter(r => r !== role) });
                        }
                      }}
                    />
                    {formatRole(role)}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className={styles.formGroup}>
            <label>Message Template</label>
            <input
              type="text"
              placeholder="Auto-credited waves for your contribution"
              value={autoWavesFormData.reason_template}
              onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, reason_template: e.target.value })}
            />
            <span className={styles.fieldHint}>Use {'{{waves_amount}}'} for the amount, {'{{user_name}}'} for recipient name</span>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={autoWavesFormData.is_active}
                onChange={(e) => setAutoWavesFormData({ ...autoWavesFormData, is_active: e.target.checked })}
              />
              Enable this rule
            </label>
          </div>
        </div>
      </FormModal>

      {/* Delete Auto-Waves Rule Modal */}
      <Modal
        isOpen={showDeleteAutoWavesModal}
        onClose={() => { setShowDeleteAutoWavesModal(false); setSelectedAutoWavesRule(null); }}
        title="Delete Auto-Waves Rule"
      >
        <div className={styles.addUserForm}>
          {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.deleteConfirmation}>
            <p>Are you sure you want to delete <strong>{selectedAutoWavesRule?.name}</strong>?</p>
            <p className={styles.deleteWarning}>This rule will stop distributing waves automatically. Execution history will be preserved.</p>
          </div>
          <div className={styles.formActions}>
            <Button variant="ghost" onClick={() => { setShowDeleteAutoWavesModal(false); setSelectedAutoWavesRule(null); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteAutoWavesRule} disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete Rule'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add News Source Modal */}
      <FormModal
        isOpen={showAddNewsModal}
        onClose={() => { setShowAddNewsModal(false); resetNewsForm(); }}
        title="Add News Source"
        subtitle="Connect a news API for the daily feed"
        icon={<Newspaper size={16} />}
        onSubmit={handleAddNewsSource}
        submitLabel="Add Source"
        submitLoadingLabel="Adding…"
        submitLoading={newsSaving}
        submitDisabled={newsFormData.categories.length === 0}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Provider *</label>
            <select
              value={newsFormData.provider}
              onChange={(e) => setNewsFormData({ ...newsFormData, provider: e.target.value })}
              className={styles.select}
            >
              <option value="newsapi">NewsAPI.org - Free tier: 100 requests/day</option>
              <option value="gnews">GNews - Free tier: 100 requests/day</option>
              <option value="mediastack">Mediastack - Free tier: 500 requests/month</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>API Key *</label>
            <input
              type="password"
              placeholder="Enter your API key"
              value={newsFormData.api_key}
              onChange={(e) => setNewsFormData({ ...newsFormData, api_key: e.target.value })}
            />
            <span className={styles.fieldHint}>Get your API key from the provider's website</span>
          </div>
          <div className={styles.formGroup}>
            <label>Categories (select one or more)</label>
            <div className={styles.categoryCheckboxGroup}>
              {[
                { value: 'business', label: 'Business' },
                { value: 'technology', label: 'Technology' },
                { value: 'general', label: 'General' },
                { value: 'science', label: 'Science' },
                { value: 'health', label: 'Health' },
                { value: 'entertainment', label: 'Entertainment' },
                { value: 'sports', label: 'Sports' }
              ].map(cat => (
                <label key={cat.value} className={styles.categoryCheckbox}>
                  <input
                    type="checkbox"
                    checked={newsFormData.categories.includes(cat.value)}
                    onChange={() => toggleNewsCategory(cat.value)}
                  />
                  <span>{cat.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={newsFormData.is_enabled}
                onChange={(e) => setNewsFormData({ ...newsFormData, is_enabled: e.target.checked })}
              />
              Enable this source
            </label>
          </div>
      </FormModal>

      {/* Edit News Source Modal */}
      <FormModal
        isOpen={showEditNewsModal}
        onClose={() => { setShowEditNewsModal(false); setSelectedNewsSource(null); resetNewsForm(); }}
        title="Edit News Source"
        subtitle="Update provider key, categories, or status"
        icon={<Edit2 size={16} />}
        onSubmit={handleUpdateNewsSource}
        submitLabel="Save Changes"
        submitLoadingLabel="Saving…"
        submitLoading={newsSaving}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Provider</label>
            <select
              value={newsFormData.provider}
              onChange={(e) => setNewsFormData({ ...newsFormData, provider: e.target.value })}
              className={styles.select}
            >
              <option value="newsapi">NewsAPI.org</option>
              <option value="gnews">GNews</option>
              <option value="mediastack">Mediastack</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>API Key</label>
            <input
              type="password"
              placeholder="Enter new API key (leave blank to keep existing)"
              value={newsFormData.api_key}
              onChange={(e) => setNewsFormData({ ...newsFormData, api_key: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Categories (select one or more)</label>
            <div className={styles.categoryCheckboxGroup}>
              {[
                { value: 'business', label: 'Business' },
                { value: 'technology', label: 'Technology' },
                { value: 'general', label: 'General' },
                { value: 'science', label: 'Science' },
                { value: 'health', label: 'Health' },
                { value: 'entertainment', label: 'Entertainment' },
                { value: 'sports', label: 'Sports' }
              ].map(cat => (
                <label key={cat.value} className={styles.categoryCheckbox}>
                  <input
                    type="checkbox"
                    checked={newsFormData.categories.includes(cat.value)}
                    onChange={() => toggleNewsCategory(cat.value)}
                  />
                  <span>{cat.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={newsFormData.is_enabled}
                onChange={(e) => setNewsFormData({ ...newsFormData, is_enabled: e.target.checked })}
              />
              Enable this source
            </label>
          </div>
      </FormModal>

      {/* Add Quotes Source Modal */}
      <FormModal
        isOpen={showAddQuotesModal}
        onClose={() => { setShowAddQuotesModal(false); resetQuotesForm(); }}
        title="Add Quotes Source"
        subtitle="Connect a quotes API for the daily feed"
        icon={<Quote size={16} />}
        onSubmit={handleAddQuotesSource}
        submitLabel="Add Source"
        submitLoadingLabel="Adding…"
        submitLoading={quotesSaving}
        submitDisabled={quotesFormData.categories.length === 0}
      >
        {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Provider *</label>
            <select
              value={quotesFormData.provider}
              onChange={(e) => setQuotesFormData({ ...quotesFormData, provider: e.target.value })}
              className={styles.select}
            >
              <option value="quotable">Quotable - Free, no API key required</option>
              <option value="api-ninjas">API Ninjas - Free tier: 10,000 requests/month</option>
              <option value="zenquotes">ZenQuotes - Free, no API key required</option>
            </select>
          </div>
          {quotesFormData.provider === 'api-ninjas' && (
            <div className={styles.formGroup}>
              <label>API Key *</label>
              <input
                type="password"
                placeholder="Enter your API key"
                value={quotesFormData.api_key}
                onChange={(e) => setQuotesFormData({ ...quotesFormData, api_key: e.target.value })}
              />
            </div>
          )}
          <div className={styles.formGroup}>
            <label>Categories (select one or more)</label>
            <div className={styles.categoryCheckboxGroup}>
              {[
                { value: 'motivational', label: 'Motivational' },
                { value: 'inspirational', label: 'Inspirational' },
                { value: 'success', label: 'Success' },
                { value: 'leadership', label: 'Leadership' },
                { value: 'wisdom', label: 'Wisdom' },
                { value: 'happiness', label: 'Happiness' },
                { value: 'life', label: 'Life' }
              ].map(cat => (
                <label key={cat.value} className={styles.categoryCheckbox}>
                  <input
                    type="checkbox"
                    checked={quotesFormData.categories.includes(cat.value)}
                    onChange={() => toggleQuotesCategory(cat.value)}
                  />
                  <span>{cat.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={quotesFormData.is_enabled}
                onChange={(e) => setQuotesFormData({ ...quotesFormData, is_enabled: e.target.checked })}
              />
              Enable this source
            </label>
          </div>
      </FormModal>

      {/* Edit Quotes Source Modal */}
      <FormModal
        isOpen={showEditQuotesModal}
        onClose={() => { setShowEditQuotesModal(false); setSelectedQuotesSource(null); resetQuotesForm(); }}
        title="Edit Quotes Source"
        subtitle="Update provider key, categories, or status"
        icon={<Edit2 size={16} />}
        onSubmit={handleUpdateQuotesSource}
        submitLabel="Save Changes"
        submitLoadingLabel="Saving…"
        submitLoading={quotesSaving}
      >
          {actionError && <div className={styles.errorMessage}>{actionError}</div>}
          <div className={styles.formGroup}>
            <label>Provider</label>
            <select
              value={quotesFormData.provider}
              onChange={(e) => setQuotesFormData({ ...quotesFormData, provider: e.target.value })}
              className={styles.select}
            >
              <option value="quotable">Quotable</option>
              <option value="api-ninjas">API Ninjas</option>
              <option value="zenquotes">ZenQuotes</option>
            </select>
          </div>
          {quotesFormData.provider === 'api-ninjas' && (
            <div className={styles.formGroup}>
              <label>API Key</label>
              <input
                type="password"
                placeholder="Enter new API key (leave blank to keep existing)"
                value={quotesFormData.api_key}
                onChange={(e) => setQuotesFormData({ ...quotesFormData, api_key: e.target.value })}
              />
            </div>
          )}
          <div className={styles.formGroup}>
            <label>Categories (select one or more)</label>
            <div className={styles.categoryCheckboxGroup}>
              {[
                { value: 'motivational', label: 'Motivational' },
                { value: 'inspirational', label: 'Inspirational' },
                { value: 'success', label: 'Success' },
                { value: 'leadership', label: 'Leadership' },
                { value: 'wisdom', label: 'Wisdom' },
                { value: 'happiness', label: 'Happiness' },
                { value: 'life', label: 'Life' }
              ].map(cat => (
                <label key={cat.value} className={styles.categoryCheckbox}>
                  <input
                    type="checkbox"
                    checked={quotesFormData.categories.includes(cat.value)}
                    onChange={() => toggleQuotesCategory(cat.value)}
                  />
                  <span>{cat.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={quotesFormData.is_enabled}
                onChange={(e) => setQuotesFormData({ ...quotesFormData, is_enabled: e.target.checked })}
              />
              Enable this source
            </label>
          </div>
      </FormModal>

      {/* Success notification */}
      {actionSuccess && (
        <div className={styles.successToast}>
          {actionSuccess}
        </div>
      )}
    </div>
  );
};

export default Settings;
